// =====================================================
// api/endpoints-core.js — the ONLY API helpers the EAGER app shell may import.
//
// چرا این فایل جدا است (S-ASSESS-W3-UX2، فاز ۱ — رژیم باندل):
// Rollup هر ماژول را یکجا در یک chunk می‌نشاند، نه per-export. تا وقتی ۹ ماژولِ
// eager (AuthContext, AppShell, Breadcrumbs, term-context, cart-state و هوک‌های
// چت/اعلان/کانکت) از endpoints.js ایمپورت می‌کردند، تمامِ ~۶۰ آبجکتِ API — از
// جمله assessCenterApi و walletApi و … — به chunkِ eager (index-*.js) کشیده
// می‌شد. با جداکردنِ همین ۱۰ آبجکتی که پوستهٔ اپ واقعاً لازم دارد، بقیهٔ
// endpoints.js فقط از مسیرهای lazy قابل‌دسترس می‌ماند و از chunkِ eager بیرون
// می‌افتد (سقفِ docs/BUNDLE_BASELINE.json).
//
// قانونِ نگه‌داری: چیزی به این فایل اضافه نکنید مگر ماژولی که خارج از مرزِ
// React.lazy زندگی می‌کند به آن نیاز داشته باشد. هر API جدیدِ صفحه‌ایْ جایش
// endpoints.js است. endpoints.js همین ۱۰ آبجکت را دوباره export می‌کند، پس
// صفحه‌های lazy همچنان فقط از "endpoints.js" ایمپورت می‌کنند و هیچ call-site
// دیگری تغییر نمی‌کند.
// =====================================================

import { api, publicApi, ApiError } from "./client.js";
import { sessionStore } from "../auth/session-store.js";

// Prefix every SPA path with /api (host Caddy strips it). Mirrors client.js's
// API_PREFIX — kept here too for the one place we bypass the fetch wrapper
// (multipart upload with progress, which needs XMLHttpRequest).
const API_PREFIX = "/api";

/**
 * Multipart POST with upload-progress, used for self-hosted media uploads
 * (R-MARKET P2). The shared fetch client (client.js) has no progress events,
 * so this one call uses XMLHttpRequest directly. It attaches the access token
 * the same way client.js does, parses the JSON body, and rejects with the same
 * structured `ApiError` (so callers read `.displayMessage` exactly as elsewhere).
 * No 401-refresh retry here: a fresh access token is in hand at upload start and
 * the upload is a single short-lived request; on a 401 the caller surfaces the
 * error and the user retries (their next call refreshes the session).
 *
 * Large/slow uploads (a 2 GB HD lecture over a throttled Iran link can take many
 * minutes): we DISABLE the XHR timeout (`xhr.timeout = 0`) so the browser never
 * aborts a still-progressing upload, and we report a clean Persian message for the
 * network/timeout/abort cases so the dialog never shows a raw English "network error".
 *
 * @param {string} path        — API path WITHOUT the /api prefix (e.g. /v1/market/assets)
 * @param {FormData} formData  — the multipart body
 * @param {(pct:number)=>void} [onProgress] — 0..100 upload percentage
 */
const uploadMultipart = (path, formData, onProgress) =>
  new Promise((resolve, reject) => {
    const url = API_PREFIX + path;
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    xhr.withCredentials = true; // same-origin cookies (refresh cookie rides along)
    // No HARD client total-timeout: a large/slow (Iran) upload must not be killed mid-flight
    // while bytes are still flowing (a 2 GB lecture over a throttled link can legitimately take
    // a long time). The server (multer fileSize) + Caddy (request-body cap + proxy read/write
    // timeout) are the authoritative ceilings. 0 = no total cap on THIS in-progress request.
    // A blunt 30-min total timeout would instead abort a slow-but-progressing upload — so we use
    // the STALL WATCHDOG below (which only trips on a genuinely hung connection) instead.
    xhr.timeout = 0;

    // STALL WATCHDOG — the one failure mode the other handlers miss: the connection HANGS with
    // no bytes flowing and NO error/timeout event (a half-open socket, a proxy holding the
    // connection). With xhr.timeout=0 that would spin the progress bar forever. So: if there is
    // ZERO upload progress for STALL_MS, fail with an ACTIONABLE Persian message. The timer
    // RESETS on every progress tick, so a slow upload that keeps moving never trips it.
    const STALL_MS = 120000; // 2 min of no progress ⇒ the upload has stalled
    let stallTimer = null;
    const clearStall = () => {
      if (stallTimer) {
        clearTimeout(stallTimer);
        stallTimer = null;
      }
    };
    const armStall = () => {
      clearStall();
      stallTimer = setTimeout(() => {
        // Abort the hung request (frees the socket); onloadend then clears the timer. The
        // reject below wins (first settle), so the user sees this actionable message — not the
        // generic "network error" or an endless spinner.
        try {
          xhr.abort();
        } catch {
          /* ignore */
        }
        reject(
          new ApiError({
            status: 0,
            statusText: "stalled",
            body: {
              message:
                "بارگذاری متوقف شد (داده‌ای ارسال نمی‌شود). اتصال اینترنت یا حجم فایل را بررسی کنید و دوباره تلاش کنید؛ برای فایل‌های حجیم اتصالِ پایدار لازم است.",
            },
            url,
          }),
        );
      }, STALL_MS);
    };

    const token = sessionStore.get()?.accessToken;
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.setRequestHeader("Accept", "application/json");
    // NOTE: do NOT set Content-Type — the browser sets the multipart boundary.

    // Progress: report 0..100 (when asked) AND re-arm the stall watchdog — each tick is proof
    // the upload is still moving. Attached even without an onProgress callback so the watchdog
    // always has its heartbeat.
    if (xhr.upload) {
      xhr.upload.onprogress = (e) => {
        armStall();
        if (onProgress && e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }

    const parseBody = () => {
      const ct = xhr.getResponseHeader("content-type") || "";
      if (ct.includes("application/json")) {
        try {
          return JSON.parse(xhr.responseText);
        } catch {
          return null;
        }
      }
      return xhr.responseText || null;
    };

    // Normalize a NON-JSON error body so the dialog never shows a raw HTML proxy page.
    // A 413 commonly arrives from a reverse-proxy/body-size limit as an HTML error page
    // (not our API's JSON) → surface a clean Persian message; other non-JSON errors get a
    // generic Persian fallback. (Our API errors are JSON { message } and pass through.)
    const normalizeErrorBody = (body, status) => {
      const looksHtml = typeof body === "string" && /<\s*(?:!doctype|html)/i.test(body.trim());
      // A 413 from our API arrives as JSON (multer filter — it names the exact GB
      // ceiling); pass that through. A 413 from the reverse proxy (body-size cap)
      // arrives as a non-JSON/HTML page → give a clean Persian fallback that names
      // the (video) ceiling so the author knows the practical limit.
      if (status === 413) {
        if (body && typeof body === "object" && typeof body.message === "string") return body;
        return {
          message:
            "حجم فایل بیش از حد مجاز است (حداکثر حدود ۲ گیگابایت برای ویدئو). فایل را فشرده یا کوتاه‌تر کنید و دوباره بارگذاری کنید.",
        };
      }
      if (typeof body === "string" && looksHtml) {
        return { message: "بارگذاری ناموفق بود (پاسخ نامعتبر از سرور). دوباره تلاش کنید." };
      }
      return body;
    };

    xhr.onload = () => {
      const body = parseBody();
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body);
      } else {
        reject(
          new ApiError({
            status: xhr.status,
            statusText: xhr.statusText,
            body: normalizeErrorBody(body, xhr.status),
            url,
          }),
        );
      }
    };
    // A failed connection / dropped upload (no HTTP status). Surface a clean,
    // actionable Persian message — NOT the raw English "network error" — so the
    // in-dialog error tells the author what to do (check the connection, retry).
    xhr.onerror = () =>
      reject(
        new ApiError({
          status: 0,
          statusText: "network error",
          body: {
            message:
              "بارگذاری ناتمام ماند (قطعِ ارتباط با سرور). اتصال اینترنت را بررسی کنید و دوباره تلاش کنید. برای فایل‌های حجیم، اتصال پایدار لازم است.",
          },
          url,
        }),
      );
    // xhr.timeout is 0 (disabled) so ontimeout should never fire; we wire it anyway
    // for defensiveness in case an upstream proxy resets a very long-idle connection.
    xhr.ontimeout = () =>
      reject(
        new ApiError({
          status: 0,
          statusText: "timeout",
          body: {
            message:
              "بارگذاری بیش از حد طول کشید و قطع شد. در صورت امکان فایل را فشرده یا کوتاه‌تر کنید و با اتصال پایدارتر دوباره تلاش کنید.",
          },
          url,
        }),
      );
    xhr.onabort = () =>
      reject(
        new ApiError({
          status: 0,
          statusText: "aborted",
          body: { message: "بارگذاری لغو شد." },
          url,
        }),
      );

    // onloadend fires after ANY terminal state (load/error/abort/timeout) → clear the watchdog
    // so it can never fire once the request has settled. Then arm it and send.
    xhr.onloadend = () => clearStall();
    armStall();
    xhr.send(formData);
  });

/**
 * Raw-binary PATCH with upload-progress, used for the chunked/resumable upload protocol
 * (S-STUDIO-P1-W2). Mirrors uploadMultipart's stall-watchdog/error-handling exactly, but
 * sends a single Blob chunk (Content-Type: application/octet-stream, plus the two custom
 * X-Chunk-* headers) instead of a FormData body — the server's raw-body reader
 * (market-upload.controller.ts) expects exactly this wire shape, so json()/urlencoded()
 * middleware never touches it and the bytes reach the handler untouched.
 *
 * @param {string} path — API path WITHOUT the /api prefix
 * @param {Blob} chunk — the chunk bytes (a File.slice())
 * @param {{offset:number, sha256?:string}} meta
 * @param {(pct:number)=>void} [onProgress] — THIS chunk's 0..100 progress, not the whole file's
 */
const patchChunkBinary = (path, chunk, { offset, sha256 }, onProgress) =>
  new Promise((resolve, reject) => {
    const url = API_PREFIX + path;
    const xhr = new XMLHttpRequest();
    xhr.open("PATCH", url);
    xhr.withCredentials = true;
    xhr.timeout = 0; // same reasoning as uploadMultipart — the stall watchdog below is authoritative

    const STALL_MS = 120000;
    let stallTimer = null;
    const clearStall = () => {
      if (stallTimer) {
        clearTimeout(stallTimer);
        stallTimer = null;
      }
    };
    const armStall = () => {
      clearStall();
      stallTimer = setTimeout(() => {
        try {
          xhr.abort();
        } catch {
          /* ignore */
        }
        reject(
          new ApiError({
            status: 0,
            statusText: "stalled",
            body: {
              message:
                "بارگذاریِ این چانک متوقف شد (داده‌ای ارسال نمی‌شود). اتصال اینترنت را بررسی کنید — با اتصالِ دوباره از همین نقطه ازسرگیری می‌شود.",
            },
            url,
          }),
        );
      }, STALL_MS);
    };

    const token = sessionStore.get()?.accessToken;
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.setRequestHeader("Accept", "application/json");
    xhr.setRequestHeader("Content-Type", "application/octet-stream");
    xhr.setRequestHeader("X-Chunk-Offset", String(offset));
    if (sha256) xhr.setRequestHeader("X-Chunk-Sha256", sha256);

    if (xhr.upload) {
      xhr.upload.onprogress = (e) => {
        armStall();
        if (onProgress && e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }

    const parseBody = () => {
      const ct = xhr.getResponseHeader("content-type") || "";
      if (ct.includes("application/json")) {
        try {
          return JSON.parse(xhr.responseText);
        } catch {
          return null;
        }
      }
      return xhr.responseText || null;
    };
    const normalizeErrorBody = (body) => {
      const looksHtml = typeof body === "string" && /<\s*(?:!doctype|html)/i.test(body.trim());
      if (typeof body === "string" && looksHtml) {
        return { message: "ارسالِ چانک ناموفق بود (پاسخ نامعتبر از سرور). دوباره تلاش کنید." };
      }
      return body;
    };

    xhr.onload = () => {
      const body = parseBody();
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body);
      } else {
        reject(new ApiError({ status: xhr.status, statusText: xhr.statusText, body: normalizeErrorBody(body), url }));
      }
    };
    xhr.onerror = () =>
      reject(
        new ApiError({
          status: 0,
          statusText: "network error",
          body: {
            message:
              "ارسالِ چانک ناتمام ماند (قطعِ ارتباط با سرور). اتصال اینترنت را بررسی کنید — بارگذاری از همین نقطه ازسرگیری می‌شود.",
          },
          url,
        }),
      );
    xhr.ontimeout = () =>
      reject(
        new ApiError({
          status: 0,
          statusText: "timeout",
          body: { message: "ارسالِ چانک بیش از حد طول کشید و قطع شد. دوباره تلاش کنید." },
          url,
        }),
      );
    xhr.onabort = () =>
      reject(new ApiError({ status: 0, statusText: "aborted", body: { message: "بارگذاری لغو شد." }, url }));

    xhr.onloadend = () => clearStall();
    armStall();
    xhr.send(chunk);
  });

// ---------- auth ----------
export const authApi = {
  login: ({ tenantSlug, email, password }) =>
    publicApi.post("/v1/auth/login", { tenantSlug, email, password }),
  register: ({ tenantSlug, email, password, fullName }) =>
    publicApi.post("/v1/auth/register", { tenantSlug, email, password, fullName }),
  refresh: (refreshToken) =>
    publicApi.post("/v1/auth/refresh", refreshToken ? { refreshToken } : undefined),
  // logout is authenticated (not @Public) → api.post so the access token is sent
  // (auto-refreshes if expired) and the server revokes + clears the HttpOnly
  // refresh cookie. The cookie itself rides along automatically (same-origin).
  logout: (refreshToken) =>
    api.post("/v1/auth/logout", refreshToken ? { refreshToken } : undefined),
  me: () => api.get("/v1/auth/me"),
  // R-Notif (D100) — password-claim flow (public, token-based). validateClaim
  // is a non-consuming check for the landing page; claim sets the password +
  // returns a session.
  validateClaim: (token) => publicApi.post("/v1/auth/claim/validate", { token }),
  claim: ({ token, password }) => publicApi.post("/v1/auth/claim", { token, password }),
  // R-AUTH-3 — forgot/reset password. forgotPassword is anti-enumeration
  // (always { ok: true }); the reset token travels in the body.
  forgotPassword: ({ tenantSlug, email }) =>
    publicApi.post("/v1/auth/forgot-password", { tenantSlug, email }),
  validateReset: (token) => publicApi.post("/v1/auth/reset/validate", { token }),
  resetPassword: ({ token, password }) =>
    publicApi.post("/v1/auth/reset-password", { token, password }),
  // R-AUTH-5 — 2FA. loginTwoFactor is the public step-2; the rest are
  // authenticated 2FA management (the user is logged in to configure it).
  loginTwoFactor: ({ pendingToken, code }) =>
    publicApi.post("/v1/auth/2fa/login", { pendingToken, code }),
  twoFactor: {
    status: () => api.get("/v1/auth/2fa/status"),
    setup: () => api.post("/v1/auth/2fa/setup"),
    enable: (code) => api.post("/v1/auth/2fa/enable", { code }),
    disable: (code) => api.post("/v1/auth/2fa/disable", { code }),
  },
  // R-SESSIONS — the caller's own active refresh-token sessions (real device
  // management for the Settings → امنیت tab). All self-scoped by JWT.
  //   list         → [{ id, createdAt, lastUsedAt?, userAgent?, ip?, current:boolean }]
  //                  (current = the session behind THIS request, if determinable).
  //   revoke(id)   → revoke ONE of the caller's own sessions (404 if not theirs).
  //   revokeOthers → revoke every session EXCEPT the current → { revoked:number }.
  sessions: {
    list: () => api.get("/v1/auth/sessions"),
    revoke: (id) => api.delete("/v1/auth/sessions/" + encodeURIComponent(id)),
    revokeOthers: () => api.post("/v1/auth/sessions/revoke-others", {}),
  },
};

// ---------- catalog ----------
export const catalogApi = {
  // Phase B R1 Commit F (D62) — listSchools added. Existing listFaculties
  // gains optional schoolId filter (additive — undefined arg behaves
  // identically to the prior call shape).
  listSchools: () => api.get("/v1/schools"),
  listFaculties: ({ schoolId } = {}) =>
    api.get(
      "/v1/faculties" + (schoolId ? "?schoolId=" + encodeURIComponent(schoolId) : ""),
    ),
  listDepartments: ({ facultyId } = {}) =>
    api.get(
      "/v1/departments" + (facultyId ? "?facultyId=" + encodeURIComponent(facultyId) : ""),
    ),
  listPrograms: ({ departmentId, degreeLevel } = {}) => {
    const q = [];
    if (departmentId) q.push("departmentId=" + encodeURIComponent(departmentId));
    if (degreeLevel) q.push("degreeLevel=" + encodeURIComponent(degreeLevel));
    return api.get("/v1/programs" + (q.length ? "?" + q.join("&") : ""));
  },
  listCourses: ({ programId, level, language } = {}) => {
    const q = [];
    if (programId) q.push("programId=" + encodeURIComponent(programId));
    if (level) q.push("level=" + encodeURIComponent(level));
    if (language) q.push("language=" + encodeURIComponent(language));
    return api.get("/v1/courses" + (q.length ? "?" + q.join("&") : ""));
  },
  getCourse: (id) => api.get("/v1/courses/" + encodeURIComponent(id)),
  listCohorts: ({ programId } = {}) =>
    api.get(
      "/v1/cohorts" + (programId ? "?programId=" + encodeURIComponent(programId) : ""),
    ),
};

// ---------- class sessions ----------
export const classSessionsApi = {
  // §12 archive — saved chat + caption lines with author names (staff/host).
  transcript: (id) => api.get("/v1/class-sessions/" + encodeURIComponent(id) + "/transcript"),
  // R-SONIOX (phase 2) — the persisted live-translation transcript (kind="soniox"
  // finals only). Open to any participant who could join (the backend re-runs the
  // join gate); → Array<{ authorName, body, lang, createdAt }>. Used by the
  // «گفتگو» panel's download/save action.
  sonioxTranscript: (id) =>
    api.get("/v1/class-sessions/" + encodeURIComponent(id) + "/soniox-transcript"),
  list: ({ courseId, status } = {}) => {
    const q = [];
    if (courseId) q.push("courseId=" + encodeURIComponent(courseId));
    if (status) q.push("status=" + encodeURIComponent(status));
    return api.get("/v1/class-sessions" + (q.length ? "?" + q.join("&") : ""));
  },
  get: (id) => api.get("/v1/class-sessions/" + encodeURIComponent(id)),
  create: (body) => api.post("/v1/class-sessions", body),
  update: (id, body) => api.patch("/v1/class-sessions/" + encodeURIComponent(id), body),
  requestEnd: (id) => api.post("/v1/class-sessions/" + encodeURIComponent(id) + "/end-request", {}),
  undoEnd: (id) => api.post("/v1/class-sessions/" + encodeURIComponent(id) + "/end-undo", {}),
  attendance: (id) => api.get("/v1/class-sessions/" + encodeURIComponent(id) + "/attendance"),
  // S-16 — per-session attendance analysis: { finalized, total, present, late,
  //   absent, excused, students:[{userId,name,status}] }. Provisional while the
  //   session is live; graded after it ends + auto-reconciles.
  attendanceSummary: (id) => api.get("/v1/class-sessions/" + encodeURIComponent(id) + "/attendance-summary"),
  // MS-17 — the full «گزارش کلاس» for a session (staff/host only): { session,
  //   timing, attendance, participants:[{userId,name,email,firstJoinedAt,joins,
  //   minutes}], chat:{count,lines}, captions:{captionCount,sonioxCount},
  //   recording }. Aggregates only PERSISTED data (no live-collab artifacts).
  report: (id) => api.get("/v1/class-sessions/" + encodeURIComponent(id) + "/report"),
  playbackTranscriptDownload: (id) => api.get("/v1/class-sessions/" + encodeURIComponent(id) + "/playback-transcript.txt", { responseType: "blob" }),
  playbackTranscriptSearch: (id, query, cursor) => api.post("/v1/class-sessions/" + encodeURIComponent(id) + "/playback-transcript/search", { q: query, ...(cursor ? { cursor } : {}) }),
  playbackPollAnswer: (id, pollId, choice) =>
    api.post("/v1/class-sessions/" + encodeURIComponent(id) + "/playback-polls/" + encodeURIComponent(pollId) + "/answer", { choice }),
  playbackNotes: (id) => api.get("/v1/class-sessions/" + encodeURIComponent(id) + "/playback-notes"),
  playbackWatch: (id) => api.get("/v1/class-sessions/" + encodeURIComponent(id) + "/playback-watch"),
  samplePlaybackWatch: (id, body) => api.post("/v1/class-sessions/" + encodeURIComponent(id) + "/playback-watch", body),
  playbackMakeupPolicy: (id) => api.get("/v1/class-sessions/" + encodeURIComponent(id) + "/playback-makeup-policy"),
  configurePlaybackMakeup: (id, body) => api.put("/v1/class-sessions/" + encodeURIComponent(id) + "/playback-makeup-policy", body),
  playbackMakeupCredits: (id) => api.get("/v1/class-sessions/" + encodeURIComponent(id) + "/playback-makeup-credits"),
  claimPlaybackMakeup: (id, reason) => api.post("/v1/class-sessions/" + encodeURIComponent(id) + "/playback-makeup-credits", { reason }),
  decidePlaybackMakeup: (id, creditId, body) => api.patch("/v1/class-sessions/" + encodeURIComponent(id) + "/playback-makeup-credits/" + encodeURIComponent(creditId), body),
  createPlaybackNote: (id, body) => api.post("/v1/class-sessions/" + encodeURIComponent(id) + "/playback-notes", body),
  updatePlaybackNote: (id, noteId, body) => api.patch("/v1/class-sessions/" + encodeURIComponent(id) + "/playback-notes/" + encodeURIComponent(noteId), body),
  deletePlaybackNote: (id, noteId) => api.delete("/v1/class-sessions/" + encodeURIComponent(id) + "/playback-notes/" + encodeURIComponent(noteId)),
  replayQuestions: (id) => api.get("/v1/class-sessions/" + encodeURIComponent(id) + "/replay-questions"),
  createReplayQuestion: (id, body) => api.post("/v1/class-sessions/" + encodeURIComponent(id) + "/replay-questions", body),
  optInReplayQuestion: (id, questionId, optIn) => api.patch("/v1/class-sessions/" + encodeURIComponent(id) + "/replay-questions/" + encodeURIComponent(questionId) + "/opt-in", { optIn }),
  publishReplayQuestion: (id, questionId, publish) => api.patch("/v1/class-sessions/" + encodeURIComponent(id) + "/replay-questions/" + encodeURIComponent(questionId) + "/publication", { publish }),
  answerReplayQuestion: (id, questionId, body) => api.post("/v1/class-sessions/" + encodeURIComponent(id) + "/replay-questions/" + encodeURIComponent(questionId) + "/answers", body),
  reportReplayQuestion: (id, questionId, reason) => api.post("/v1/class-sessions/" + encodeURIComponent(id) + "/replay-questions/" + encodeURIComponent(questionId) + "/reports", { reason }),
  hideReplayQuestion: (id, questionId, hidden) => api.patch("/v1/class-sessions/" + encodeURIComponent(id) + "/replay-questions/" + encodeURIComponent(questionId) + "/hidden", { hidden }),
  replayChat: (id, cursor) => api.get("/v1/class-sessions/" + encodeURIComponent(id) + "/replay-chat" + (cursor ? "?cursor=" + encodeURIComponent(cursor) : "")),
  createReplayChat: (id, body) => api.post("/v1/class-sessions/" + encodeURIComponent(id) + "/replay-chat", body),
  reportReplayChat: (id, messageId, reason) => api.post("/v1/class-sessions/" + encodeURIComponent(id) + "/replay-chat/" + encodeURIComponent(messageId) + "/reports", { reason }),
  hideReplayChat: (id, messageId, hidden) => api.patch("/v1/class-sessions/" + encodeURIComponent(id) + "/replay-chat/" + encodeURIComponent(messageId) + "/hidden", { hidden }),
  playbackArtifacts: (id) => api.get("/v1/class-sessions/" + encodeURIComponent(id) + "/playback-artifacts"),
  configurePlaybackArtifacts: (id, learnerContributionsEnabled) => api.put("/v1/class-sessions/" + encodeURIComponent(id) + "/playback-artifacts/policy", { learnerContributionsEnabled }),
  createPlaybackArtifact: (id, body) => api.post("/v1/class-sessions/" + encodeURIComponent(id) + "/playback-artifacts", body),
  moderatePlaybackArtifact: (id, artifactId, action) => api.patch("/v1/class-sessions/" + encodeURIComponent(id) + "/playback-artifacts/" + encodeURIComponent(artifactId), { action }),
  tableTranscriptConsent: (id, tableId) => api.get("/v1/class-sessions/" + encodeURIComponent(id) + "/tables/" + encodeURIComponent(tableId) + "/transcript-consent"),
  setTableTranscriptConsent: (id, tableId, body) => api.put("/v1/class-sessions/" + encodeURIComponent(id) + "/tables/" + encodeURIComponent(tableId) + "/transcript-consent", body),
  createPlaybackRoom: (id) => api.post("/v1/class-sessions/" + encodeURIComponent(id) + "/playback-rooms"),
  playbackRoomState: (roomId) => api.get("/v1/class-sessions/playback-rooms/" + encodeURIComponent(roomId)),
  joinPlaybackRoom: (roomId) => api.post("/v1/class-sessions/playback-rooms/" + encodeURIComponent(roomId) + "/join"),
  leavePlaybackRoom: (roomId) => api.post("/v1/class-sessions/playback-rooms/" + encodeURIComponent(roomId) + "/leave"),
  invitePlaybackRoom: (roomId, userId) => api.post("/v1/class-sessions/playback-rooms/" + encodeURIComponent(roomId) + "/invitations", { userId }),
  offerPlaybackRoomHandover: (roomId, userId) => api.post("/v1/class-sessions/playback-rooms/" + encodeURIComponent(roomId) + "/handover", { userId }),
  acceptPlaybackRoomHandover: (roomId) => api.post("/v1/class-sessions/playback-rooms/" + encodeURIComponent(roomId) + "/handover/accept"),
  removePlaybackRoomMember: (roomId, userId) => api.delete("/v1/class-sessions/playback-rooms/" + encodeURIComponent(roomId) + "/members/" + encodeURIComponent(userId)),
  closePlaybackRoom: (roomId) => api.post("/v1/class-sessions/playback-rooms/" + encodeURIComponent(roomId) + "/close"),
  join: (id) => api.post("/v1/class-sessions/" + encodeURIComponent(id) + "/join"),
  leave: (id, joinLeaseId) => api.post("/v1/class-sessions/" + encodeURIComponent(id) + "/leave", { joinLeaseId }),
  analyze: (id, { task = "analyze", language = "fa" } = {}) =>
    api.post("/v1/class-sessions/" + encodeURIComponent(id) + "/analyze", { task, language }),
  // S-LIVE-P1-W1 گام ۱-الف — تلمتریِ اتصال‌پذیریِ ICE پس از وصل‌شدنِ ترنسپورتِ
  // SFU. fail-safe در سمتِ کالر: هرگز نباید کلاسِ زنده را بشکند.
  iceTelemetry: (id, body) =>
    api.post("/v1/class-sessions/" + encodeURIComponent(id) + "/ice-telemetry", body),
  // S-LIVE-P1-W1 — سقفِ ظرفیتِ کلاسِ زنده (ادمین/سوپرادمین)، به الگویِ
  // market.adminConfig/setAdminConfig. capacityConfig() → { maxConcurrentClasses,
  // maxParticipants, maxGalleryTiles }.
  capacityConfig: () => api.get("/v1/live-class/admin/capacity"),
  setCapacityConfig: (body) => api.put("/v1/live-class/admin/capacity", body),
};

// ---------- assessments + submissions ----------
export const assessmentsApi = {
  // S-21 — examMode ("true"|"false") splits the hub's تکالیف/آزمون‌ها tabs server-side.
  // E1 — marketCourseId scopes the list to a market course (the studio tab).
  list: ({ courseId, marketCourseId, status, kind, examMode } = {}) => {
    const q = [];
    if (courseId) q.push("courseId=" + encodeURIComponent(courseId));
    if (marketCourseId) q.push("marketCourseId=" + encodeURIComponent(marketCourseId));
    if (status) q.push("status=" + encodeURIComponent(status));
    if (kind) q.push("kind=" + encodeURIComponent(kind));
    if (examMode != null) q.push("examMode=" + encodeURIComponent(examMode));
    return api.get("/v1/assessments" + (q.length ? "?" + q.join("&") : ""));
  },
  get: (id) => api.get("/v1/assessments/" + encodeURIComponent(id)),
  create: (body) => api.post("/v1/assessments", body),
  // S-12 — general assessment PATCH (title/config + exam question-pool poolConfig).
  update: (id, body) => api.patch("/v1/assessments/" + encodeURIComponent(id), body),
  addQuestion: (assessmentId, body) =>
    api.post("/v1/assessments/" + encodeURIComponent(assessmentId) + "/questions", body),
  // R-EXAM-1b — edit/remove an existing question (questions live at /v1/questions/:id).
  updateQuestion: (questionId, body) =>
    api.patch("/v1/questions/" + encodeURIComponent(questionId), body),
  deleteQuestion: (questionId) =>
    api.delete("/v1/questions/" + encodeURIComponent(questionId)),
  publish: (id) => api.patch("/v1/assessments/" + encodeURIComponent(id), { status: "published" }),
  // S-11 — clone a reusable QuestionBankItem into a Question on this quiz.
  addQuestionFromBank: (assessmentId, bankItemId) =>
    api.post(
      "/v1/assessments/" +
        encodeURIComponent(assessmentId) +
        "/questions/from-bank/" +
        encodeURIComponent(bankItemId),
      {},
    ),
};

// ---------- identity (Phase B R3.a D68 + D69) ----------
//
// Profile / Student / Instructor surface. All Profile endpoints use the
// SelfOrAdminGuard (D69 primitive): a non-admin caller can only access
// their own resource; admins can access any in the tenant. Backend
// returns 403 on cross-user attempts by non-admins.

export const profileApi = {
  // Self-service (uses JWT to resolve target user)
  getOwn: () => api.get("/v1/profile"),
  updateOwn: (data) => api.patch("/v1/profile", data),

  // R-SETTINGS — PUBLIC profile read (privacy-gated). Backed by the real
  // @Public() GET /v1/profile/public/:userId. The server applies the owner's
  // privacy settings and returns ONLY allowed fields:
  //   • profileVisibility "private"  → 404 for everyone (no-leak).
  //   • profileVisibility "members"  → an AUTHENTICATED, same-tenant caller may
  //     read it; a guest gets 401. So when the viewer has a session we go through
  //     the AUTHENTICATED `api` client (it attaches the bearer + auto-refreshes a
  //     401); a logged-out viewer uses `publicApi` (no header), and a "public"
  //     profile still returns 200 for them. An unknown/inactive user → 404.
  // One call serves all three viewers (guest / member / owner). Throttled 60/h.
  getPublic: (userId) => {
    const path = "/v1/profile/public/" + encodeURIComponent(userId);
    return sessionStore.get()?.accessToken ? api.get(path) : publicApi.get(path);
  },

  // SELF-ONLY avatar upload (R-profile-rework). Mirrors the market COURSE COVER
  // pattern: multipart `file` (PNG/JPEG/WebP ≤5MB), stored under a PUBLIC avatars/
  // subtree, returns the FULL profile row (PROFILE_SELECT shape) with avatarUrl set
  // to a token-FREE public url that loads in a plain same-origin <img src>. The
  // server reuses the SAME self guard as the other /v1/profile routes (caller can
  // only set their OWN avatar). onProgress(pct 0..100) drives the progress bar. A
  // bad MIME/size → a Persian 400 on the ApiError; an over-cap file → a 413 (multer).
  uploadAvatar: (file, onProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    return uploadMultipart("/v1/profile/avatar", fd, onProgress);
  },

  // Admin listing — powers /admin/profiles
  listAdmin: () => api.get("/v1/profiles"),

  // SelfOrAdmin — :userId can be self (any role) or any user (admin only)
  getByUserId: (userId) => api.get("/v1/users/" + encodeURIComponent(userId) + "/profile"),
  updateByUserId: (userId, data) =>
    api.patch("/v1/users/" + encodeURIComponent(userId) + "/profile", data),
  // W1 (D-2026-06-14) — admin appoints a ریاست دانشکده to a faculty (scope).
  setFacultyScope: (userId, facultyId) =>
    api.patch("/v1/users/" + encodeURIComponent(userId) + "/faculty-scope", { facultyId }),
};

// R-SIS-1 — academic terms (نیم‌سال) + process windows. Reads (list/active/
// get) are open to any authenticated user (navbar term switcher §3.1.5 +
// every term-gated process reads the active term); mutations are admin /
// super_admin only (enforced server-side).
export const termsApi = {
  list: () => api.get("/v1/terms"),
  getActive: () => api.get("/v1/terms/active"),
  get: (id) => api.get("/v1/terms/" + encodeURIComponent(id)),
  create: (data) => api.post("/v1/terms", data),
  update: (id, data) => api.patch("/v1/terms/" + encodeURIComponent(id), data),
  transition: (id, to) =>
    api.post("/v1/terms/" + encodeURIComponent(id) + "/transition", { to }),
  setWindows: (id, windows) =>
    api.patch("/v1/terms/" + encodeURIComponent(id) + "/windows", { windows }),
  delete: (id) => api.delete("/v1/terms/" + encodeURIComponent(id)),
};

// R-CHAT-1 §18.2 — self-hosted messenger (any authed user). DM + text messages;
// the service enforces tenant scope + conversation membership. R-CHAT-1b polls
// these for near-real-time; R-CHAT-1c swaps in WebSocket delivery.
export const chatApi = {
  conversations: () => api.get("/v1/chat/conversations"),
  contacts: (q) => api.get("/v1/chat/contacts" + (q ? "?q=" + encodeURIComponent(q) : "")),
  createDm: (userId) => api.post("/v1/chat/conversations/dm", { userId }),
  getConversation: (id) => api.get("/v1/chat/conversations/" + encodeURIComponent(id)),
  // R-CHAT M7 — message history (member-gated, client-safe hydration). Two cursor
  // modes (a single fetch is ONE direction):
  //   { before } → the page OLDER than the cursor (scroll-up; ascending in the page).
  //   { after }  → messages NEWER than the cursor (ISO timestamp OR a messageId),
  //                ASCENDING. Used on socket RECONNECT to backfill the gap missed
  //                while disconnected; nextCursor is the last message ID (drain by
  //                re-calling with it while hasMore). `before` wins if BOTH are passed.
  // → { messages, hasMore, nextCursor }.
  messages: (id, { before, after, limit } = {}) => {
    const q = [];
    if (before) q.push("before=" + encodeURIComponent(before));
    else if (after) q.push("after=" + encodeURIComponent(after));
    if (limit) q.push("limit=" + encodeURIComponent(limit));
    return api.get("/v1/chat/conversations/" + encodeURIComponent(id) + "/messages" + (q.length ? "?" + q.join("&") : ""));
  },
  // R-CHAT @MENTION — the send route accepts an OPTIONAL mentions:[User.ids] of
  // members the message @-mentions. The BE validates each id is a CURRENT member of
  // the conversation (drops non-members silently) and, for each valid mentioned member
  // that is NOT the sender (and is not chat-muted), creates a coalesced/idempotent
  // in-app NotificationLog row (link → /messages?c=:id). Transient — no migration, no
  // column; the payload MAY echo mentions:[] but the FE does not depend on persistence.
  send: (id, body, replyToId, mentions, asQuestion) => {
    const payload = { body };
    if (replyToId) payload.replyToId = replyToId;
    if (Array.isArray(mentions) && mentions.length > 0) payload.mentions = mentions;
    // S4 — structured classroom question (groups/channels; the BE degrades a DM
    // "question" to a normal message silently).
    if (asQuestion === true) payload.asQuestion = true;
    return api.post("/v1/chat/conversations/" + encodeURIComponent(id) + "/messages", payload);
  },
  markRead: (id) => api.post("/v1/chat/conversations/" + encodeURIComponent(id) + "/read", {}),
  // R-CHAT-2 message features.
  editMessage: (id, body) => api.patch("/v1/chat/messages/" + encodeURIComponent(id), { body }),
  deleteMessage: (id) => api.delete("/v1/chat/messages/" + encodeURIComponent(id)),
  react: (id, emoji) => api.post("/v1/chat/messages/" + encodeURIComponent(id) + "/react", { emoji }),
  // R-CHAT-2 — groups + channels.
  createGroup: (title, memberIds) => api.post("/v1/chat/groups", { title, memberIds }),
  createChannel: (data) => api.post("/v1/chat/channels", data), // { title, description?, memberIds? }
  addMembers: (id, memberIds) => api.post("/v1/chat/conversations/" + encodeURIComponent(id) + "/members", { memberIds }),
  removeMember: (id, userId) => api.delete("/v1/chat/conversations/" + encodeURIComponent(id) + "/members/" + encodeURIComponent(userId)),
  setRole: (id, userId, role) => api.post("/v1/chat/conversations/" + encodeURIComponent(id) + "/members/" + encodeURIComponent(userId) + "/role", { role }),
  leave: (id) => api.post("/v1/chat/conversations/" + encodeURIComponent(id) + "/leave", {}),
  update: (id, data) => api.patch("/v1/chat/conversations/" + encodeURIComponent(id), data),
  // S-22 wave 2 — mute/unmute a conversation FOR THE CALLER (بی‌صدا). A muted
  // conversation is skipped by BOTH in-app notification fan-outs (the coalesced
  // per-conversation chat.message row AND the @mention ping). → { conversationId, muted }.
  setMute: (id, muted) => api.post("/v1/chat/conversations/" + encodeURIComponent(id) + "/mute", { muted: !!muted }),
  // R-CHAT §18.2.4 — public channel discovery + self-subscribe (unsubscribe = leave).
  channels: () => api.get("/v1/chat/channels"),
  subscribe: (id) => api.post("/v1/chat/conversations/" + encodeURIComponent(id) + "/subscribe", {}),
  // R-CHAT-3 — auto class spaces from course sections.
  mySections: () => api.get("/v1/chat/my-sections"),
  provisionSection: (id) => api.post("/v1/chat/sections/" + encodeURIComponent(id) + "/conversations", {}),
  // S-21 — member-gated lookup (instructor or REGISTERED student of the section)
  // of the provisioned class spaces: { groupId, channelId, provisioned }. Feeds
  // the course hub's «گروه درس» deep-link (/messages?c=<groupId>).
  sectionConversation: (id) => api.get("/v1/chat/sections/" + encodeURIComponent(id) + "/conversation"),

  // R-CHAT M2 — message search across the caller's MEMBER conversations (server
  // membership + tenant gated). q (>=2 chars) + optional conversationId (restrict
  // to one thread) + before (ISO keyset cursor) + limit (1..50). →
  //   { results:[{ messageId, conversationId, conversationTitle, senderId,
  //                senderName, kind, body, snippet (<mark>…</mark>), createdAt,
  //                mine }], hasMore, nextCursor }.
  // ADVANCED FILTERS (composable, all optional + membership-scoped server-side):
  //   senderId → restrict to one sender (a member User.id).
  //   kind     → "media" (image+voice) | "file" | "question" (S4 Q&A) | "all" (default).
  //   from/to  → ISO date-range bounds (S4 — the FE sends computed quick-ranges).
  search: ({ q, conversationId, before, limit, senderId, kind, from, to } = {}) => {
    const params = [];
    if (q) params.push("q=" + encodeURIComponent(q));
    if (conversationId) params.push("conversationId=" + encodeURIComponent(conversationId));
    if (before) params.push("before=" + encodeURIComponent(before));
    if (limit) params.push("limit=" + encodeURIComponent(limit));
    if (senderId) params.push("senderId=" + encodeURIComponent(senderId));
    if (kind && kind !== "all") params.push("kind=" + encodeURIComponent(kind));
    if (from) params.push("from=" + encodeURIComponent(from));
    if (to) params.push("to=" + encodeURIComponent(to));
    return api.get("/v1/chat/search" + (params.length ? "?" + params.join("&") : ""));
  },

  // ----- S4 — classroom Q&A + group polls -----
  // Pick / clear the accepted answer of a question message (owner/admin only).
  acceptAnswer: (messageId, answerId) =>
    api.post("/v1/chat/messages/" + encodeURIComponent(messageId) + "/accept-answer", answerId ? { answerId } : {}),
  // The Q&A list of a conversation: { questions:[message + answersCount], hasMore, nextCursor }.
  questions: (conversationId, { answered, before, limit } = {}) => {
    const params = [];
    if (answered === true || answered === "true") params.push("answered=true");
    if (answered === false || answered === "false") params.push("answered=false");
    if (before) params.push("before=" + encodeURIComponent(before));
    if (limit) params.push("limit=" + encodeURIComponent(limit));
    return api.get(
      "/v1/chat/conversations/" + encodeURIComponent(conversationId) + "/questions" + (params.length ? "?" + params.join("&") : ""),
    );
  },
  // Create a poll (a new kind="poll" message; body = question). → the enriched message.
  createPoll: (conversationId, { question, options, multi }) =>
    api.post("/v1/chat/conversations/" + encodeURIComponent(conversationId) + "/polls", {
      question,
      options,
      ...(multi ? { multi: true } : {}),
    }),
  // Vote / re-vote (option indexes; last-write-wins). → the enriched poll message.
  votePoll: (pollId, choices) => api.post("/v1/chat/polls/" + encodeURIComponent(pollId) + "/vote", { choices }),
  // Close voting (creator or conversation owner/admin). → the enriched poll message.
  closePoll: (pollId) => api.post("/v1/chat/polls/" + encodeURIComponent(pollId) + "/close", {}),

  // SEEN-BY (read receipts roster). Member-gated server-side (a non-member / cross-tenant
  // / missing message → 403/404 no-leak). →
  //   GROUP/CHANNEL: { kind, readers:[{ userId, fullName, avatarUrl, readAt }], readCount }
  //                  (the OTHER members whose lastReadAt >= the message createdAt, newest first).
  //   DM:            { kind:"dm", read:boolean, readAt:ISO|null, readers:[…] }.
  // The list is capped server-side (~500). Used by the «دیده‌شده توسط» sheet on own messages.
  readBy: (messageId) => api.get("/v1/chat/messages/" + encodeURIComponent(messageId) + "/read-by"),

  // R-CHAT M3 — batch presence read. userIds: string[] → comma-joined query.
  //   → [{ userId, status:"online"|"offline", lastSeenAt: ISO|null }].
  // lastSeenAt is privacy-gated server-side (Profile.preferences.privacy
  // .showLastSeen): "contacts" (default) reveals it only to accepted connections /
  // shared-conversation members; otherwise it comes back null (status only).
  presence: (userIds) =>
    api.get(
      "/v1/chat/presence?userIds=" + encodeURIComponent((userIds || []).filter(Boolean).join(",")),
    ),

  // R-CHAT M4 — global unread summary for the «پیام‌ها» nav badge.
  //   → { totalUnread } (from per-member lastReadAt — same source as the list).
  unreadSummary: () => api.get("/v1/chat/unread-summary"),

  // R-CHAT media (Telegram-class attachments). Upload an image/file to a conversation the
  // caller is a member of; multipart field "file" + optional "body" caption. The backend
  // IMMEDIATELY creates the message (an attachment is a message with kind!="text") and fans
  // it out via the SAME socket "message" event, so the POST resolves to the enriched message
  // (kind:"image"|"file", attachmentUrl, attachmentThumbUrl, attachmentName, attachmentMeta).
  // Uses uploadMultipart for an XHR upload-progress bar (the fetch client has no progress);
  // onProgress(pct 0..100) drives the composer progress UI. A bad MIME/kind/size → a Persian
  // 400 on the ApiError; an over-ceiling file → a 413 (multer) naming the chat file cap (MB).
  // M7 — a VOICE note (audio/webm|ogg|mpeg|mp4 from MediaRecorder) goes through this SAME
  // route; the server classifies it as kind "voice" (token-served, inline-disposition <audio>).
  uploadAttachment: (conversationId, file, onProgress, body) => {
    const fd = new FormData();
    fd.append("file", file);
    if (body && String(body).trim()) fd.append("body", String(body).trim());
    return uploadMultipart(
      "/v1/chat/conversations/" + encodeURIComponent(conversationId) + "/attachments",
      fd,
      onProgress,
    );
  },

  // R-CHAT M5a — PIN MESSAGES. Authority is server-enforced (group/channel
  // owner/admin; DM either member) + tenant + membership gated; a 404 never leaks
  // a foreign message/conversation. A pin/unpin fans out the NEW socket event
  // "message:pin" { conversationId, messageId, pinned } to the room, and the
  // ~10-pin cap rejects with a Persian 400.
  //   pin(messageId)    → the updated message (kind/body/attachment* + pinnedAt/pinnedBy).
  //   unpin(messageId)  → { ok:true } (idempotent on an already-unpinned message).
  pin: (messageId) => api.post("/v1/chat/messages/" + encodeURIComponent(messageId) + "/pin", {}),
  unpin: (messageId) => api.delete("/v1/chat/messages/" + encodeURIComponent(messageId) + "/pin"),
  // → the conversation's pinned messages (member-gated), newest pin first; each row
  //   is the SAME client-safe message shape history returns (token-minted
  //   attachmentUrl/thumbUrl + publicAttachmentMeta — never storageKey).
  listPins: async (conversationId) => {
    const result = await api.get("/v1/chat/conversations/" + encodeURIComponent(conversationId) + "/pins");
    // The API returns { messages, count }; all message-pane callers consume rows.
    return Array.isArray(result) ? result : Array.isArray(result?.messages) ? result.messages : [];
  },

  // R-CHAT M5b — SHARED-MEDIA GALLERY. Cursor-paginated read over the
  // conversation's image|file messages (member-gated), newest first, returning the
  // SAME client-safe message shape as history (token-minted attachmentUrl/thumbUrl
  // + publicAttachmentMeta). type ∈ "image" | "file"; before is an ISO keyset
  // cursor; limit 1..50. → { messages:[…], hasMore, nextCursor }.
  listMedia: (conversationId, { type, before, limit } = {}) => {
    const q = [];
    if (type) q.push("type=" + encodeURIComponent(type));
    if (before) q.push("before=" + encodeURIComponent(before));
    if (limit) q.push("limit=" + encodeURIComponent(limit));
    return api.get(
      "/v1/chat/conversations/" + encodeURIComponent(conversationId) + "/media" + (q.length ? "?" + q.join("&") : ""),
    );
  },

  // S-22 wave 2 — COURSE-aggregated shared-media library. Aggregates image|file
  // messages across ALL of the course's section chat spaces (section_group +
  // section_channel) the caller is a member of (membership-scoped server-side —
  // a student sees their section's media, an instructor every section they run,
  // a stranger an empty page; a missing course → 404). Same client-safe message
  // shape + keyset paging as listMedia. type ∈ "image"|"file"; before = ISO cursor.
  // → { messages:[…], hasMore, nextCursor }.
  courseMedia: (courseId, { type, before, limit } = {}) => {
    const q = [];
    if (type) q.push("type=" + encodeURIComponent(type));
    if (before) q.push("before=" + encodeURIComponent(before));
    if (limit) q.push("limit=" + encodeURIComponent(limit));
    return api.get(
      "/v1/chat/course/" + encodeURIComponent(courseId) + "/media" + (q.length ? "?" + q.join("&") : ""),
    );
  },

  // R-CHAT M6b — REPORT A MESSAGE. Member-gated server-side (you can only report a
  // message in a conversation you're in) + @AuditAction; one OPEN report per
  // (reporter, message) is idempotent (P2002-guarded → the server resolves a
  // duplicate to the existing open report, never a hard error). reason is optional
  // (a short free-text note). A foreign message/conversation → 404 (no-leak); a
  // non-member → 403 with a Persian message on the ApiError. →
  //   { id, status:"open", messageId, conversationId, reason?, createdAt }.
  report: (messageId, reason) =>
    api.post(
      "/v1/chat/messages/" + encodeURIComponent(messageId) + "/report",
      reason && String(reason).trim() ? { reason: String(reason).trim() } : {},
    ),

  // R-CHAT M8 — FORWARD a message into another conversation the caller is a member of.
  // BE forward contract (chat.controller ForwardDto):
  //   POST /v1/chat/messages/:id/forward  { targetConversationId }
  // DOUBLE-gated server-side: the caller must be a member of BOTH the SOURCE (to read
  // the message) AND the TARGET (to post); the target's post-permission (channel) + DM
  // block gate apply; a missing/cross-tenant source OR target → 404/403 no-leak. The
  // server creates a brand-NEW message in the target (copying the body + any
  // image/file/voice attachment BYTES into the target's subtree, re-minting a fresh
  // per-viewer token-gated url on read; replyTo/pin/reactions are NOT carried) stamped
  // with forwardedFromName + forwardedFromId (the ORIGINAL author, snapshotted; a
  // re-forward still credits the true origin). It fans the new message out via the SAME
  // socket "message" event (no new event) + the same in-app notification as a normal
  // send, and RESOLVES to the enriched, client-safe message (same shape history
  // returns). The FE appends it if the target is the open thread (de-duped by id vs the
  // socket echo).
  forward: (messageId, targetConversationId) =>
    api.post(
      "/v1/chat/messages/" + encodeURIComponent(messageId) + "/forward",
      { targetConversationId },
    ),

  // R-CHAT (multi-select) — BATCH FORWARD up to 50 messages into one target conversation.
  //   POST /v1/chat/messages/forward-batch  { messageIds:string[] (<=50), targetConversationId }
  // Same gating as the single forward, applied per source message: the caller must be a
  // member of EACH source message's conversation AND the target (assertCanDm / member-gated).
  // Reuses the single-forward internals (snapshot forwardedFromName/Id, copy attachment bytes).
  // Source order is preserved. → the created messages array (same client-safe shape as forward).
  forwardBatch: (messageIds, targetConversationId) =>
    api.post("/v1/chat/messages/forward-batch", {
      messageIds: (messageIds || []).slice(0, 50),
      targetConversationId,
    }),

  // R-CHAT (multi-select) — BATCH DELETE up to 50 messages.
  //   POST /v1/chat/messages/delete-batch  { messageIds:string[] (<=50) }
  // Each message is deleted only if the caller may delete it (same rule as the single
  // DELETE /messages/:id — own message, or group/channel owner/admin). Ones it can't
  // delete are silently skipped. → { deleted:string[] } (only the ids actually deleted).
  deleteBatch: (messageIds) =>
    api.post("/v1/chat/messages/delete-batch", {
      messageIds: (messageIds || []).slice(0, 50),
    }),

  // R-CHAT (my mentions) — the caller's own @-mention notifications, newest first.
  //   GET /v1/chat/mentions?cursor=  →
  //     { results:[{ conversationId, conversationTitle, messageId, senderName,
  //                  snippet, createdAt, readAt }], nextCursor }
  // Source = NotificationLog (tenant + userId=caller + kind="in_app" + template="chat.mention");
  // the BE joins the Message (for the snippet) + Conversation (for the title) and drops rows
  // whose message/convo was deleted. Tenant-scoped, only the caller's mentions (no leak). Page ~20.
  mentions: (cursor) =>
    api.get("/v1/chat/mentions" + (cursor ? "?cursor=" + encodeURIComponent(cursor) : "")),

  // S-22 wave 2 — mention DIGEST: a per-conversation roll-up of the caller's
  // UNREAD @mentions (grouped counts + latest snippet), newest-active first.
  // Same source + gating as mentions() but grouped. →
  //   { groups:[{ conversationId, conversationTitle, count, latestAt,
  //               latestSnippet, latestSenderName }], totalUnread }.
  mentionsSummary: () => api.get("/v1/chat/mentions/summary"),
};

// R-CONNECT — academic social-connect layer (§social). Two primitives over the
// real /v1/connect/* API: FOLLOW (one-way, no approval) + CONNECT (symmetric,
// needs acceptance → an accepted connection unlocks direct DM), plus BLOCK
// (overrides everything). Every mutation is @AuditAction + P2002-guarded
// server-side; cross-tenant / self / missing target → 404 (no-leak); a block in
// either direction → 403 on connect/follow attempts. The single policy source
// (connect-policy.ts canDirectMessage/canConnect/canFollow) is resolved into the
// relationship payload the UI reads — the FE never re-implements the policy.
export const connectApi = {
  // --- CONNECT (symmetric; needs acceptance) ---
  // request → { status:"pending"|"accepted", connectionId, dmConversationId? }
  // (auto-accepts when a reverse request was already pending).
  request: ({ userId, note } = {}) =>
    api.post("/v1/connect/connections", { userId, ...(note ? { note } : {}) }),
  // respond (addressee only) → { status:"accepted"|"declined", connectionId,
  // dmConversationId? } (accept unlocks DM).
  respond: (connectionId, action) =>
    api.post("/v1/connect/connections/" + encodeURIComponent(connectionId) + "/respond", { action }),
  // remove an accepted connection OR cancel a pending outgoing request (by the
  // OTHER user's id, not the connectionId) → { ok:true }.
  removeConnection: (userId) =>
    api.delete("/v1/connect/connections/" + encodeURIComponent(userId)),

  // --- FOLLOW (one-way; no approval; idempotent) ---
  follow: (userId) => api.post("/v1/connect/follow/" + encodeURIComponent(userId), {}),
  unfollow: (userId) => api.delete("/v1/connect/follow/" + encodeURIComponent(userId)),

  // --- BLOCK (overrides everything; idempotent; tears down connection + the
  //     blocked user's follow) ---
  block: (userId) => api.post("/v1/connect/block/" + encodeURIComponent(userId), {}),
  unblock: (userId) => api.delete("/v1/connect/block/" + encodeURIComponent(userId)),

  // --- LISTS (no body) ---
  // → [{ connectionId, userId, fullName, avatarUrl, connectedAt }]
  connections: () => api.get("/v1/connect/connections"),
  // → [{ connectionId, note, requestedAt, userId, fullName, avatarUrl }]
  incoming: () => api.get("/v1/connect/requests/incoming"),
  outgoing: () => api.get("/v1/connect/requests/outgoing"),
  // → [{ userId, fullName, avatarUrl, since }]
  following: () => api.get("/v1/connect/following"),
  followers: () => api.get("/v1/connect/followers"),

  // --- RELATIONSHIP RESOLVER ---
  // → the relationship payload: { connected, pendingOutgoing, pendingIncoming,
  //   following, followsYou, blocked, blockedBy, canDirectMessage, canConnect,
  //   canFollow }.
  relationship: (userId) =>
    api.get("/v1/connect/relationship/" + encodeURIComponent(userId)),

  // --- DISCOVERY (connect-scoped people search + suggestions + block list) ---
  // PEOPLE SEARCH — the connect-scoped analog of peopleApi.search: same tenant,
  // EXCLUDES self + cross-tenant, never leaks a blocked-by user's detail beyond a
  // neutral row, and ATTACHES the relationship (same shape relationshipTo returns)
  // so each row can render the right action inline. q>=2 (else the server answers
  // an empty page). Cursor-paginated.
  //   → { results:[{ userId, fullName, avatarUrl, role, relationship }], nextCursor }
  search: ({ q, role, cursor } = {}) => {
    const p = [];
    if (q) p.push("q=" + encodeURIComponent(q));
    if (role) p.push("role=" + encodeURIComponent(role));
    if (cursor) p.push("cursor=" + encodeURIComponent(cursor));
    return api.get("/v1/connect/search" + (p.length ? "?" + p.join("&") : ""));
  },
  // SUGGESTIONS — up to 12 same-tenant users who share a CourseSection OR an
  // existing conversation with the caller but are NOT yet connected/following/
  // blocked. `reason` is a short Persian string («هم‌کلاسی در …» / «در یک گفت‌وگوی
  // مشترک»). Cheap, capped, no paging.
  //   → { results:[{ userId, fullName, avatarUrl, role, reason }] }
  suggestions: () => api.get("/v1/connect/suggestions"),
  // BLOCK LIST — the users the CALLER has blocked (unblock via the existing
  // DELETE /v1/connect/block/:userId).
  //   → { results:[{ userId, fullName, avatarUrl, blockedAt }] }
  blocks: () => api.get("/v1/connect/blocks"),

  // --- ENDORSEMENTS (skill endorsements on a profile's expertise/topics) ---
  // Idempotent toggle-ON: endorse a single skill (a short string, ≤60) on
  // another same-tenant user. P2002-guarded server-side, so re-endorsing the
  // same skill is a no-op → { endorsed:true }. Can't endorse yourself (the
  // server no-ops/400s; the UI hides the control on your own profile anyway).
  endorse: ({ userId, skill }) =>
    api.post("/v1/connect/endorse", { userId, skill }),
  // Toggle-OFF: remove your endorsement of that skill → { endorsed:false }
  // (200 even if it wasn't there). userId + skill travel as query params.
  unendorse: ({ userId, skill }) =>
    api.delete(
      "/v1/connect/endorse?userId=" +
        encodeURIComponent(userId) +
        "&skill=" +
        encodeURIComponent(skill),
    ),

  // --- PROFILE EXTRAS (privacy-gated rich aggregate for /u/:userId) ---
  // GET → { connectionCount, followerCount, followingCount, mutualConnections,
  //   sharedSections:[{id,title}], endorsements:[{skill,count,endorsedByMe}],
  //   achievements:[{...public}], recentActivity:[{...public}] }.
  // Server respects the target's profileVisibility: a private target (that is
  // not the viewer + not connected) returns ONLY the counts (no sections /
  // activity), and achievements/recentActivity are public-safe items only.
  profileExtras: (userId) =>
    api.get("/v1/connect/profile-extras/" + encodeURIComponent(userId)),
};

// ---------- notifications (R-NAV-5 — in-app inbox over R-Notif) ----------
// The bell + /inbox read the current user's own notifications.
//   list   → { items:[{id,type,title,body,kind,read,createdAt}], total,
//             unreadCount, page, limit }
//   markRead / markAllRead → flip readAt server-side (no body needed).
export const notificationsApi = {
  list: ({ unread, page, limit } = {}) => {
    const q = [];
    if (unread) q.push("unread=true");
    if (page) q.push("page=" + encodeURIComponent(page));
    if (limit) q.push("limit=" + encodeURIComponent(limit));
    return api.get("/v1/notifications" + (q.length ? "?" + q.join("&") : ""));
  },
  markRead: (id) => api.patch("/v1/notifications/" + encodeURIComponent(id) + "/read"),
  markAllRead: () => api.patch("/v1/notifications/read-all"),
};

// R-MARKET P1 — «بازار دانش» authoring studio (course STRUCTURE only). Author-
// owned (instructor/content_manager + admin/super_admin oversight; the public
// marketplace browse tab is P4 — NOT here). Every write 404s a non-owner non-
// admin (no-leak) and 409s a non-editable course (only draft/changes_requested
// are editable). The chart hangs off the course's CURRENT version; reorder takes
// the FULL ordered id list for the parent. Each mutation returns the full nested
// course detail ({ …course, currentVersion:{ modules:[{ lessons:[{ sections }] }] } }),
// so the editor can render straight from the response with no extra refetch.
export const marketApi = {
  // --- course ---
  coursesMine: () => api.get("/v1/market/courses/mine"),
  course: (id) => api.get("/v1/market/courses/" + encodeURIComponent(id)),
  createCourse: (body) => api.post("/v1/market/courses", body),
  updateCourse: (id, body) =>
    api.patch("/v1/market/courses/" + encodeURIComponent(id), body),
  deleteCourse: (id) => api.delete("/v1/market/courses/" + encodeURIComponent(id)),
  // --- modules (on the course's current version) ---
  addModule: (id, body) =>
    api.post("/v1/market/courses/" + encodeURIComponent(id) + "/modules", body),
  updateModule: (id, moduleId, body) =>
    api.patch(
      "/v1/market/courses/" + encodeURIComponent(id) + "/modules/" + encodeURIComponent(moduleId),
      body,
    ),
  deleteModule: (id, moduleId) =>
    api.delete(
      "/v1/market/courses/" + encodeURIComponent(id) + "/modules/" + encodeURIComponent(moduleId),
    ),
  reorderModules: (id, orderedIds) =>
    api.patch("/v1/market/courses/" + encodeURIComponent(id) + "/modules/reorder", { orderedIds }),
  // --- lessons (under a module) ---
  addLesson: (id, moduleId, body) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/modules/" + encodeURIComponent(moduleId) + "/lessons",
      body,
    ),
  updateLesson: (id, lessonId, body) =>
    api.patch(
      "/v1/market/courses/" + encodeURIComponent(id) + "/lessons/" + encodeURIComponent(lessonId),
      body,
    ),
  deleteLesson: (id, lessonId) =>
    api.delete(
      "/v1/market/courses/" + encodeURIComponent(id) + "/lessons/" + encodeURIComponent(lessonId),
    ),
  reorderLessons: (id, moduleId, orderedIds) =>
    api.patch(
      "/v1/market/courses/" + encodeURIComponent(id) + "/modules/" + encodeURIComponent(moduleId) + "/lessons/reorder",
      { orderedIds },
    ),
  // --- sections (under a lesson) ---
  addSection: (id, lessonId, body) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/lessons/" + encodeURIComponent(lessonId) + "/sections",
      body,
    ),
  updateSection: (id, sectionId, body) =>
    api.patch(
      "/v1/market/courses/" + encodeURIComponent(id) + "/sections/" + encodeURIComponent(sectionId),
      body,
    ),
  deleteSection: (id, sectionId) =>
    api.delete(
      "/v1/market/courses/" + encodeURIComponent(id) + "/sections/" + encodeURIComponent(sectionId),
    ),
  reorderSections: (id, lessonId, orderedIds) =>
    api.patch(
      "/v1/market/courses/" + encodeURIComponent(id) + "/lessons/" + encodeURIComponent(lessonId) + "/sections/reorder",
      { orderedIds },
    ),

  // --- AI-assist (R-MARKET §8 — author conveniences; dev-mock-first, author-only) ---
  // Every call is author-only (own course; cross → 404), per-user hourly throttled
  // (429 with a Persian message), STRICTLY validated server-side, and audited to
  // AiInteractionLog by AiBridgeService. The generate calls MUTATE NOTHING — they
  // propose a DRAFT the author must review/edit; only applyOutline writes the chart.
  //
  //   §8a — generateOutline → { outline:{ modules:[{title, lessons:[{title,
  //         sections:[{title, contentType}]}]}] }, meta }. The model reasons over the
  //         STORED course title/description/level; `moduleCount` (1..20) only hints
  //         the size. The author previews + edits, then APPLIES it.
  aiOutline: (id, body) =>
    api.post("/v1/market/courses/" + encodeURIComponent(id) + "/ai-outline", body || {}),
  //   §8a — applyOutline → the FULL course detail (the chart batch-created on the DRAFT
  //         working version; content refs left empty for the author). The WHOLE outline
  //         is re-validated server-side — the client preview is never trusted.
  applyOutline: (id, modules) =>
    api.post("/v1/market/courses/" + encodeURIComponent(id) + "/apply-outline", { modules }),
  //   §8b — generateInteractive → { content:<ValidatedInteractive>, meta }. `kind` ∈
  //         multichoice|flashcards|blanks|text; `topic` (required) + `instruction`
  //         (optional) + `count`. The builder PARSES content into its model + fills the
  //         manual form so the author refines before saving.
  aiInteractive: (id, sectionId, body) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/sections/" + encodeURIComponent(sectionId) + "/ai-interactive",
      body,
    ),
  //   §8b — generateQuiz → { quiz:{ questions:[{question, options, allowMultiple}] }, meta }.
  aiQuiz: (id, sectionId, body) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/sections/" + encodeURIComponent(sectionId) + "/ai-quiz",
      body,
    ),

  //   W5 — studio copilot. SIX advisory generators under the flat /market/ai/* prefix
  //   (ids ride in the BODY, so no path encoding is needed). Every one of them returns a
  //   DRAFT + `meta` and writes NOTHING server-side: the author copies/edits and saves by
  //   hand through the ordinary routes. A price suggestion in particular is advisory only —
  //   there is deliberately no "apply" endpoint for it.
  aiDescribe: (courseId, body) =>
    api.post("/v1/market/ai/describe", { courseId, ...(body || {}) }),
  aiObjectives: (courseId, body) =>
    api.post("/v1/market/ai/objectives", { courseId, ...(body || {}) }),
  aiPricingSuggest: (courseId) =>
    api.post("/v1/market/ai/pricing-suggest", { courseId }),
  aiReviewReplyDraft: (courseId, body) =>
    api.post("/v1/market/ai/review-reply", { courseId, ...(body || {}) }),
  aiQnaDraft: (courseId, body) =>
    api.post("/v1/market/ai/qna-draft", { courseId, ...(body || {}) }),
  //   Grounded in the section's OWN lesson content. E5 widened the grounding: a caption
  //   (VTT/SRT) is used when the section has one, otherwise the section's rich text is
  //   flattened and used instead — so the button is offered for any section that has
  //   `captionAssetId` OR `richTextBody`, and only a section with NEITHER answers 400.
  //   The response's `source.kind` ("caption" | "richtext") says which one grounded it.
  aiQuizFromTranscript: (courseId, sectionId, body) =>
    api.post("/v1/market/ai/quiz-from-transcript", {
      courseId,
      sectionId,
      ...(body || {}),
    }),

  // --- E5 — instructor analytics (/v1/market/insights/*) ---------------------
  //   Author-scoped: every route resolves the caller's OWN authorship of the course
  //   server-side; a foreign id is a 404 (no-leak), never a 403. The at-risk list
  //   carries a NAME + a course-scoped pseudonym and NEVER an email — contacting a
  //   student goes through the in-platform messenger (insightsMessageStudent), and
  //   the CSV export is fully anonymous (pseudonym only) by construction.
  //   • insights            → { courseId, title, funnel, assessments[], grading, atRisk:{count} }
  //   • insightsItemAnalysis→ the S-12 per-question engine (difficulty/discrimination/
  //                           optionDistribution) for ONE exam linked to this course
  //   • insightsAtRisk      → { courseId, criteria:{progressPercent,graceDays},
  //                             items:[{ pseudonymId, name, progressPercent, daysEnrolled,
  //                             failedRequired, exhausted, awaitingGrade, reasons }] }
  //   • insightsAtRiskCsv   → the SAME list as text/csv (a STRING here — the caller wraps
  //                           it in a Blob to download; the api client returns text as-is)
  insights: (courseId) =>
    api.get("/v1/market/insights/courses/" + encodeURIComponent(courseId)),
  insightsItemAnalysis: (courseId, assessmentId) =>
    api.get(
      "/v1/market/insights/courses/" + encodeURIComponent(courseId) +
      "/items/" + encodeURIComponent(assessmentId),
    ),
  insightsAtRisk: (courseId) =>
    api.get("/v1/market/insights/courses/" + encodeURIComponent(courseId) + "/at-risk"),
  insightsAtRiskCsv: (courseId) =>
    api.get("/v1/market/insights/courses/" + encodeURIComponent(courseId) + "/at-risk.csv"),
  //   Send ONE message to a flagged student by PSEUDONYM (the author never learns the
  //   student's id or address). Opens/reuses the course conversation → { conversationId }.
  insightsMessageStudent: (courseId, pseudonymId, body) =>
    api.post(
      "/v1/market/insights/courses/" + encodeURIComponent(courseId) +
      "/at-risk/" + encodeURIComponent(pseudonymId) + "/message",
      { body },
    ),
  //   Tenant-wide teaching-quality roll-up (admin|super_admin). Course-level aggregate —
  //   no buyer is named anywhere in it.
  insightsAdminOverview: () => api.get("/v1/market/insights/admin/overview"),
  insightsAdminOverviewCsv: () => api.get("/v1/market/insights/admin/overview.csv"),

  // --- E5 — the BUYER's two AI advisories (/v1/market/ai-learning/*) ---------
  //   Authority is the caller's OWN enrollment (not a role), enforced server-side.
  //   • aiExplainAnswer → { explanation, hint, nextStep, meta }. Only for a question the
  //     buyer answered WRONG, only when the author opted the exam in (aiExplainEnabled)
  //     AND the exam's showAnswersPolicy already reveals the key to them — otherwise 403.
  //   • aiStudyPath → { summary, steps:[{ lessonId, sectionId, title, why }], meta }. The
  //     steps are always sections of the caller's own course chart (the server resolves
  //     every step itself), advisory only — nothing is unlocked or applied.
  aiExplainAnswer: (courseId, assessmentId, questionId) =>
    api.post(
      "/v1/market/ai-learning/courses/" + encodeURIComponent(courseId) +
      "/exams/" + encodeURIComponent(assessmentId) +
      "/questions/" + encodeURIComponent(questionId) + "/explain",
      {},
    ),
  aiStudyPath: (courseId) =>
    api.post(
      "/v1/market/ai-learning/courses/" + encodeURIComponent(courseId) + "/study-path",
      {},
    ),

  // --- assets (R-MARKET P2 — self-hosted content upload + access-gated stream) ---
  // Upload a media file to the server's own disk. `type` ∈ video|slide|document|audio|image.
  // Returns the MarketAsset view ({ id, type, fileName, sizeBytes, mimeType, durationSeconds, … }).
  // onProgress(pct 0..100) drives the in-dialog progress bar.
  uploadAsset: ({ file, type, durationSeconds } = {}, onProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("type", type);
    if (durationSeconds != null) fd.append("durationSeconds", String(durationSeconds));
    return uploadMultipart("/v1/market/assets", fd, onProgress);
  },
  // The caller's OWN assets (light list, newest first) — backs the "reuse from library" picker.
  assetsMine: () => api.get("/v1/market/assets/mine"),
  // Soft-delete an asset the caller owns (409 if used by a published course).
  deleteAsset: (id) => api.delete("/v1/market/assets/" + encodeURIComponent(id)),
  // Mint a short-lived, access-gated media token → { token, url, expiresAt }. The `url`
  // is the API-relative stream path (NO /api prefix); prepend "/api" before a <video>/<iframe> src.
  assetAccessToken: (id) =>
    api.get("/v1/market/assets/" + encodeURIComponent(id) + "/access-token"),

  // --- S-STUDIO-P1-W2 §5-3 — چانک/ازسرگیری‌پذیر (فقط ویدئو، مصرف‌کنندهٔ RecordStudio.tsx) ---
  // Same three-check storage gate as uploadAsset, evaluated against the DECLARED size before
  // a single chunk lands. initChunkedUpload opens the session → putChunkedUpload sends ONE
  // chunk (raw binary; offset required, hash optional) → statusChunkedUpload answers "how much
  // do you already have" for resume-on-reload → finishChunkedUpload assembles the MarketAsset
  // (idempotent — a second call for the same uploadId returns the same asset, never a
  // duplicate; see MarketAssetService.finishChunkedUpload).
  initChunkedUpload: ({ type, fileName, declaredSize, mimeType, clientFingerprint }) =>
    api.post("/v1/market/uploads", { type, fileName, declaredSize, mimeType, clientFingerprint }),
  // `chunk` is a Blob (a File.slice()). onProgress reports THIS chunk's 0..100, not the
  // whole file's — the caller (chunk-upload-manager) combines it with the chunk index.
  putChunkedUpload: (uploadId, chunk, { offset, sha256 } = {}, onProgress) =>
    patchChunkBinary(
      "/v1/market/uploads/" + encodeURIComponent(uploadId),
      chunk,
      { offset, sha256 },
      onProgress,
    ),
  statusChunkedUpload: (uploadId) => api.get("/v1/market/uploads/" + encodeURIComponent(uploadId)),
  finishChunkedUpload: (uploadId, { checksum, durationSeconds } = {}) =>
    api.post("/v1/market/uploads/" + encodeURIComponent(uploadId) + "/finish", {
      checksum,
      durationSeconds,
    }),

  // --- S-MKT-PRO-W3 — «پریمیر-لایت» فاز ۱: non-destructive timeline (EDL) ------
  // A MarketEditProject is a JSON edit-decision-list over ONE source asset; the uploaded
  // media is NEVER mutated (rendering lands in W6). Every route is author-only and scoped
  // to the caller's OWN course + OWN project — a foreign id is a 404 (no-leak), never 403.
  //
  //   listEditProjects(courseId, sourceAssetId?) → EditProjectView[] (newest first)
  //   createEditProject(courseId, { sourceAssetId, title?, sourceDurationSeconds? })
  //     → a project seeded with ONE full-length segment (nothing cut yet).
  //   updateEditProject(courseId, projectId, { title?, edl? }) — `edl` is the WHOLE
  //     document ({version:1, sourceDurationSeconds, segments:[…]}); the server re-validates
  //     ordering/overlap/bounds and 400s in Persian. Blocked once a render is queued.
  editProjects: (courseId, sourceAssetId) =>
    api.get(
      "/v1/market/courses/" + encodeURIComponent(courseId) + "/edit-projects" +
        (sourceAssetId ? "?sourceAssetId=" + encodeURIComponent(sourceAssetId) : ""),
    ),
  createEditProject: (courseId, body) =>
    api.post("/v1/market/courses/" + encodeURIComponent(courseId) + "/edit-projects", body),
  editProject: (courseId, projectId) =>
    api.get(
      "/v1/market/courses/" + encodeURIComponent(courseId) + "/edit-projects/" +
        encodeURIComponent(projectId),
    ),
  updateEditProject: (courseId, projectId, body) =>
    api.patch(
      "/v1/market/courses/" + encodeURIComponent(courseId) + "/edit-projects/" +
        encodeURIComponent(projectId),
      body,
    ),
  deleteEditProject: (courseId, projectId) =>
    api.delete(
      "/v1/market/courses/" + encodeURIComponent(courseId) + "/edit-projects/" +
        encodeURIComponent(projectId),
    ),
  //   renderEditProject(courseId, projectId) — S-MKT-PRO W6 «خروجی گرفتن»: queue the ffmpeg
  //     render of the CURRENT EDL. Returns the project with status "render_queued"; poll
  //     editProject() until it turns "rendered" and carries renderedAssetId (a BRAND-NEW
  //     asset — the source recording is never touched). 400 in Persian when the owner has
  //     not enabled the media pipeline on this server, or when a render is already running.
  renderEditProject: (courseId, projectId) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(courseId) + "/edit-projects/" +
        encodeURIComponent(projectId) + "/render",
      {},
    ),

  // --- S-MKT-PRO-W7 — رونوشت/زیرنویس خودکار (/v1/market/transcripts/*) --------
  // AUTHOR-ONLY, section-scoped, and deliberately absent from the playback path: a learner
  // pressing play can never cause a provider call (owner policy §5). A foreign section id is a
  // no-leak 404, like everywhere else in this module.
  //
  //   transcriptStatus(sectionId) → { enabled, status, transcriptId, provider, approximate,
  //     error, languages:[{language,status,source}] }. Pure read — never triggers anything.
  //   transcriptCues(sectionId, language?) → { language, approximate, cues:[{start,end,text}] }
  //     in MILLISECONDS, to PREFILL the caption editor. Reading them publishes nothing: the
  //     author reviews and saves through the normal caption path (hard rule ۹).
  //   requestTranscript(sectionId) — the ONLY call in the app that can start a paid ASR job.
  //     Already transcribed ⇒ it returns the existing transcript and spends nothing.
  //   requestTranscriptTranslation(sectionId, language) — translates the STORED TEXT; no audio
  //     is ever re-sent. Idempotent per language.
  transcriptStatus: (sectionId) =>
    api.get("/v1/market/transcripts/section/" + encodeURIComponent(sectionId)),
  transcriptCues: (sectionId, language) =>
    api.get(
      "/v1/market/transcripts/section/" + encodeURIComponent(sectionId) + "/cues" +
        (language ? "?language=" + encodeURIComponent(language) : ""),
    ),
  requestTranscript: (sectionId) =>
    api.post("/v1/market/transcripts/section/" + encodeURIComponent(sectionId) + "/request", {}),
  //   requestTranscriptTranslation(sectionId, language, refreshStale?) — translates the STORED
  //     TEXT; no audio is ever re-sent. `refreshStale` (W3B) is the ONLY way a reviewed track
  //     is refreshed, and even then the engine keeps every human-approved cue.
  //   reviewCaptionTranslation(sectionId, language, cues) — W3B: writes the corrector's Arabic
  //     back onto the translation row and flags the approved cues, so the next translation
  //     never re-buys a line a human already fixed.
  requestTranscriptTranslation: (sectionId, language, refreshStale) =>
    api.post("/v1/market/transcripts/section/" + encodeURIComponent(sectionId) + "/translate", {
      language,
      ...(refreshStale ? { refreshStale: true } : {}),
    }),
  reviewCaptionTranslation: (sectionId, language, cues) =>
    api.post("/v1/market/transcripts/section/" + encodeURIComponent(sectionId) + "/review", {
      language,
      cues,
    }),

  // --- review + publish workflow (R-MARKET P3 — doc §1.3 state machine + §6) ---
  // Each call returns the FULL nested course detail (the P3 transitions reuse the P1
  // buildDetailView), so the editor re-renders straight from the response. The
  // author detail now also carries latestReviewDecision / latestReviewNote /
  // latestReviewDecidedAt so the author sees WHY a course is changes_requested/rejected.
  //
  // AUTHOR (own course → 404 else): submit / publish / new-version.
  //   submitCourse — draft|changes_requested → in_review. On a 400 the server returns
  //     { message, missing:[…] } listing the SPECIFIC pre-submit gaps (§6.1); the
  //     ApiError carries them so the caller can show the missing-items list in-dialog.
  submitCourse: (id) => api.post("/v1/market/courses/" + encodeURIComponent(id) + "/submit", {}),
  //   publishCourse — approved → published (or new-version swap §6.4).
  publishCourse: (id) => api.post("/v1/market/courses/" + encodeURIComponent(id) + "/publish", {}),
  //   newVersion — clone the published version → a draft v2 (§6.4); returns the v2 detail.
  newVersion: (id) => api.post("/v1/market/courses/" + encodeURIComponent(id) + "/new-version", {}),
  //   cloneCourse (R-MARKET Wave 1 §11b) — duplicate the author's OWN published course into a
  //     brand-new DRAFT course (fresh v1, deep-copied chart, title «… (رونوشت)»). The original
  //     course + its buyers/enrollments are untouched. Returns the NEW course's full detail
  //     (status:"draft", owned by the caller). 201 Created; 409 if the source isn't published;
  //     no-leak 404 for a cross-author/cross-tenant source.
  cloneCourse: (id) => api.post("/v1/market/courses/" + encodeURIComponent(id) + "/clone", {}),
  //   readiness (S-MKT-PRO-W1) — ADVISORY publish-readiness checklist for the studio shell +
  //     creation wizard: { items:[{key,label,ok,required}], readyToSubmit, requiredOk,
  //     requiredTotal }. `required` items mirror the submit gate one-to-one; the rest are
  //     quality recommendations. Read-only — the hard gate stays submitCourse.
  readiness: (id) => api.get("/v1/market/courses/" + encodeURIComponent(id) + "/readiness"),

  // --- E1 (S-MKT-PRO زیراپیک E) — in-course exercises/exams (studio tab «آزمون‌ها») ---
  //   assessmentsOverview — the tab payload: { assessments, links, versionId, editable }.
  //     `assessments` are the course's MARKET-owned Assessment rows (courseId null);
  //     `links` are the WORKING version's live attach rows (course/module/lesson level).
  assessmentsOverview: (id) =>
    api.get("/v1/market/courses/" + encodeURIComponent(id) + "/assessments"),
  //   createAssessment — create a MARKET assessment (no university courseId; heavy
  //     proctoring keys are rejected server-side). Returns the created Assessment row.
  createAssessment: (id, body) =>
    api.post("/v1/market/courses/" + encodeURIComponent(id) + "/assessments", body),
  //   attach CRUD — one live link per (version, assessment); level = exactly one of
  //     course (no anchors) | module (moduleId) | lesson (lessonId). Mutations require the
  //     editable working version (published ⇒ its open draft) and return the fresh payload.
  createAssessmentLink: (id, body) =>
    api.post("/v1/market/courses/" + encodeURIComponent(id) + "/assessment-links", body),
  updateAssessmentLink: (id, linkId, body) =>
    api.patch(
      "/v1/market/courses/" + encodeURIComponent(id) + "/assessment-links/" + encodeURIComponent(linkId),
      body,
    ),
  deleteAssessmentLink: (id, linkId) =>
    api.delete(
      "/v1/market/courses/" + encodeURIComponent(id) + "/assessment-links/" + encodeURIComponent(linkId),
    ),

  // --- E3 (S-MKT-PRO زیراپیک E) — چرخهٔ تلاش خریدار + صف تصحیح استاد ---
  //   myCourseAssessments — the BUYER's panel for a purchased course: every live link on the
  //     current version + this buyer's own state per item { status, attemptsUsed/Remaining,
  //     bestScore, passed, canStart, reviewAvailable, answersVisible }. The attempt itself
  //     runs through the SHARED exam API (examsApi) — no market-only engine.
  myCourseAssessments: (id) =>
    api.get("/v1/market/my-courses/" + encodeURIComponent(id) + "/assessments"),
  //   myAssessmentReview — کارنامهٔ یک آزمون: score + attempt history + the buyer's own
  //     answers; the answer key/explanations/per-option feedback appear ONLY when the link's
  //     showAnswersPolicy allows it right now (answersVisible tells the UI which shape came).
  myAssessmentReview: (id, assessmentId) =>
    api.get(
      "/v1/market/my-courses/" + encodeURIComponent(id) + "/assessments/" +
        encodeURIComponent(assessmentId) + "/review",
    ),
  //   examQueue — the author's grading queue for a course (status: submitted|graded|all).
  examQueue: (id, status) =>
    api.get(
      "/v1/market/courses/" + encodeURIComponent(id) + "/exam-queue" +
        (status ? "?status=" + encodeURIComponent(status) : ""),
    ),
  //   gradeSubmission — human grade (0..100 or rubric criteriaScores) on a buyer submission.
  gradeSubmission: (id, submissionId, body) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/submissions/" +
        encodeURIComponent(submissionId) + "/grade",
      body,
    ),
  //   aiGradeDraftSubmission — AI grading DRAFT for the author (never auto-applied: the
  //     envelope carries source/confidence/humanReviewRequired and a human decides).
  aiGradeDraftSubmission: (id, submissionId) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/submissions/" +
        encodeURIComponent(submissionId) + "/ai-grade-draft",
      {},
    ),

  // --- E4 (S-MKT-PRO زیراپیک E) — ارتباط با مدرس + کارنامهٔ دانشجو ---
  //   askInstructor — پرسش خصوصی از مدرس دوره. Unlike courseQuestions (a PUBLIC forum),
  //     this opens/uses the buyer's own conversation with the author in the EXISTING
  //     messenger; the server prepends a context line (دوره › درس › آزمون) it builds
  //     itself from the optional lessonId/assessmentId. Returns { conversationId,
  //     messageId, created, contextLine } so the UI can link straight to the thread.
  askInstructor: (id, body) =>
    api.post("/v1/market/my-courses/" + encodeURIComponent(id) + "/ask-instructor", body),
  //   courseReportCard — کارنامهٔ خریدار در یک دوره: هر سنجه با تاریخچهٔ تلاش‌ها،
  //     بهترین نمره (مبنای گیت) در کنار آخرین نمره، نقاط ضعف بر اساس تگِ پرسش‌ها و
  //     مقایسهٔ ناشناسِ کلاس. مقایسه فقط «باند» است — هیچ میانگین خام و هیچ n در پاسخ
  //     نیست (تصمیم مالک: جلوگیری از حملهٔ تفاضلی روی نمرهٔ هم‌کلاسی‌ها).
  courseReportCard: (id) =>
    api.get("/v1/market/my-courses/" + encodeURIComponent(id) + "/report-card"),

  // --- cover image (R-MARKET studio fix) — author-only PUBLIC marketing image ---
  // Upload a cover (PNG/JPG/WebP ≤5MB) as multipart `file`. The server stores it under the
  // PUBLIC covers/ subtree, sets coverImageUrl to a token-FREE public url (catalog/cards show
  // it to guests), and returns the FULL course detail (so the editor re-renders straight off
  // the response). onProgress(pct 0..100) drives the in-dialog progress bar. A bad MIME/size
  // → a Persian 400 on the ApiError (shown in-dialog); an over-cap file → a 413 (multer).
  uploadCover: (id, file, onProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    return uploadMultipart("/v1/market/courses/" + encodeURIComponent(id) + "/cover", fd, onProgress);
  },

  // REVIEWER (content_manager + admin/super_admin oversight):
  //   reviewQueue — every in_review course in the tenant (light: title, author,
  //     submittedAt, module/lesson/section counts).
  reviewQueue: () => api.get("/v1/market/review-queue"),
  //   reviewCourse — full course + the SUBMITTED version chart (same detail shape as
  //     the author's editor; the reviewer reads it read-only). Author may also read it.
  reviewCourse: (id) => api.get("/v1/market/courses/" + encodeURIComponent(id) + "/review"),
  //   decideReview — record the decision { decision: approve|changes|reject, note? }.
  //     note REQUIRED (server 400) for changes/reject. in_review → approved|
  //     changes_requested|rejected.
  decideReview: (id, body) =>
    api.post("/v1/market/courses/" + encodeURIComponent(id) + "/review", body),

  // MARKET-ADMIN (admin/super_admin): suspend / unsuspend / archive a published course.
  //   suspendCourse — published → suspended ({ reason } required, server 400 else).
  suspendCourse: (id, body) =>
    api.post("/v1/market/courses/" + encodeURIComponent(id) + "/suspend", body),
  //   unsuspendCourse — suspended → published.
  unsuspendCourse: (id) =>
    api.post("/v1/market/courses/" + encodeURIComponent(id) + "/unsuspend", {}),
  //   archiveCourse — published|approved|suspended → archived ({ reason? } optional).
  archiveCourse: (id, body) =>
    api.post("/v1/market/courses/" + encodeURIComponent(id) + "/archive", body || {}),

  // --- PUBLIC marketplace (R-MARKET P4 — doc §7) ---
  // The browse surface EVERY role + a logged-out GUEST can reach. ALL of these use
  // publicApi (NO auth) and a REQUIRED tenantSlug (the established public pattern —
  // mirrors programsApi/plansApi). The backend exposes ONLY published-course
  // metadata + the chart STRUCTURE (paid sections LOCKED) + free-preview sections;
  // it never leaks paid bytes. A guest mints a free-preview media token via
  // previewToken() (the existing /assets/:id/stream?t= validates it) — used with the
  // SAME SectionViewer the studio/player use (the access-gate decides who streams).
  //
  // catalog({ tenantSlug, q?, category?, level?, language?, sort?, page?, limit? })
  //   → { items:[{ id, title, authorName, coverImageUrl, level, language, avgRating,
  //       reviewCount, enrollmentCount, publishedAt }], page, limit, total, totalPages }.
  //   sort ∈ popular|newest|rating. (free/priceMax are accepted server-side but a
  //   no-op until pricing lands in P5 — not sent here.)
  catalog: ({ tenantSlug, q, category, level, language, sort, page, limit } = {}) => {
    const p = new URLSearchParams();
    p.set("tenantSlug", tenantSlug || "");
    if (q) p.set("q", q);
    if (category) p.set("category", category);
    if (level) p.set("level", level);
    if (language) p.set("language", language);
    if (sort) p.set("sort", sort);
    if (page) p.set("page", String(page));
    if (limit) p.set("limit", String(limit));
    return publicApi.get("/v1/market/catalog?" + p.toString());
  },
  // A published course's public detail: header + author + the FULL chart (paid
  // sections carry locked:true, no content refs) + aggregate counts + author's
  // other courses. 404 if the course is missing / not published / cross-tenant.
  coursePublic: (id, tenantSlug) =>
    publicApi.get(
      "/v1/market/courses/" + encodeURIComponent(id) + "/public?tenantSlug=" +
        encodeURIComponent(tenantSlug || ""),
    ),
  // The free-preview sections (with the refs needed to render them via SectionViewer:
  // richTextBody / externalUrl / assetId + needsMediaToken). ONLY isFreePreview ones.
  previewSections: (id, tenantSlug) =>
    publicApi.get(
      "/v1/market/courses/" + encodeURIComponent(id) + "/preview-sections?tenantSlug=" +
        encodeURIComponent(tenantSlug || ""),
    ),
  // Mint a GUEST media token for a free-preview section's asset → { token, url,
  // expiresAt }. 404 unless the section is free, asset-backed + under a published
  // course. Prepend "/api" (streamSrc) before using `url` as a <video>/<audio>/<iframe> src.
  // R-MARKET CAPTIONS — pass asset="caption" to mint for the section's caption track
  // instead of its media bytes (gated identically); default "media" preserves the old call.
  // S-MKT-PRO-W7 — `lang` picks WHICH caption language to mint (asset="caption" only);
  // omitted ⇒ the Persian original, i.e. the pre-W7 call is byte-for-byte unchanged.
  previewToken: (sectionId, tenantSlug, asset, lang) =>
    publicApi.get(
      "/v1/market/sections/" + encodeURIComponent(sectionId) + "/preview-token?tenantSlug=" +
        encodeURIComponent(tenantSlug || "") +
        (asset && asset !== "media" ? "&asset=" + encodeURIComponent(asset) : "") +
        (asset === "caption" && lang ? "&lang=" + encodeURIComponent(lang) : ""),
    ),
  // Rating distribution + reviews for a published course → { average, count,
  // distribution:{1..5}, items:[] }. items is empty until reviews ship (P5/P6).
  courseReviews: (id, tenantSlug) =>
    publicApi.get(
      "/v1/market/courses/" + encodeURIComponent(id) + "/reviews?tenantSlug=" +
        encodeURIComponent(tenantSlug || ""),
    ),

  // --- PRICING + PURCHASE + ECONOMICS (R-MARKET P5 — doc §5 + §8) ---
  // PRICING (author/oversight, own course → 404 else). The PUT sets the model
  // («رایگان»/«یک‌باره») + the whole-rial price; the GET reads it back (null-shaped
  // when never priced). The public price for buyers/guests rides on coursePublic()
  // (pricingModel + priceRial) — these author endpoints are JWT-guarded.
  //   setPricing(id, { model:"free"|"one_time", priceRial? }) → { marketCourseId, model, priceRial }
  setPricing: (id, body) =>
    api.put("/v1/market/courses/" + encodeURIComponent(id) + "/pricing", body),
  //   getPricing(id) → { marketCourseId, model, priceRial }  (model/priceRial null if unset)
  getPricing: (id) => api.get("/v1/market/courses/" + encodeURIComponent(id) + "/pricing"),

  // ADMIN economics config (admin/super_admin): revenue-share % to the university +
  // refund policy (window days + max progress %) + tax placeholder.
  //   adminConfig() → { revenueSharePercentToUniversity, refundWindowDays,
  //                     refundMaxProgressPercent, taxPercentage,
  //                     holdbackDays, payoutMinRial }
  //   W8 adds the last two (both 0 ⇒ OFF = exactly the pre-W8 behaviour): days a settled
  //   share is held before it becomes payable, and the minimum an author may request.
  adminConfig: () => api.get("/v1/market/admin/config"),
  setAdminConfig: (body) => api.put("/v1/market/admin/config", body),
  // S-STUDIO-P1-W1 — سیاستِ ذخیره‌سازیِ استودیو (ادمین/سوپرادمین)، به الگویِ
  // capacityConfig/setCapacityConfig بالا. storageUsage() → مصرفِ واقعیِ بایت
  // (سراسری + به‌تفکیکِ هر استاد)، فقط خواندنی.
  storageConfig: () => api.get("/v1/market/admin/storage"),
  setStorageConfig: (body) => api.put("/v1/market/admin/storage", body),
  storageUsage: () => api.get("/v1/market/admin/storage/usage"),
  // Author-readable revenue-share % for the studio pricing panel (admin/config is
  // admin-only and 403s an author). Returns { revenueSharePercentToUniversity }.
  shareInfo: () => api.get("/v1/market/share-info"),

  // PURCHASE (any authed buyer). Direct purchase — NO cart. The server guards
  // self-purchase / already-enrolled / unpublished / no-pricing (all 409, no-leak 404).
  //   free  ⇒ { free:true, purchaseId, status:"completed", enrolled:true } (instant)
  //   paid  ⇒ { free:false, purchaseId, paymentId, redirectUrl, isMock, provider, amount }
  //           — redirect the browser to redirectUrl (the existing gateway/mock-confirm
  //           flow; on confirm the callback grants access + books the §5.3 ledger entry).
  //   R-MARKET EXT — an optional body { couponCode } applies a validated discount; the
  //   server re-validates + books the §5.3 ledger on the DISCOUNTED price (an invalid
  //   coupon aborts with a 400). Omit the body for a full-price purchase.
  //   S-MKT-PRO-W4 — the body also accepts:
  //     payMethod:"wallet"  ⇒ pay NOW from the platform wallet (no gateway redirect):
  //       → { free:false, payMethod:"wallet", purchaseId, status:"completed", enrolled:true,
  //           pricePaidRial, taxRial, walletBalanceRial }. 409 if the flag is off or the
  //       balance is short. Default (omitted) = "gateway" — exactly today's flow.
  //     idempotencyKey:"…"  ⇒ re-submitting the SAME key replays the original checkout
  //       instead of charging twice (the replay carries { replayed:true }).
  purchase: (id, body) =>
    api.post("/v1/market/courses/" + encodeURIComponent(id) + "/purchase", body || {}),

  // REFUND a COMPLETED paid purchase (buyer for own purchase, OR admin). The server
  // enforces the time-window + max-progress policy; an admin may pass { override:true }
  // to bypass it. → { ok, purchaseId, status:"refunded", refundLedgerEntryId,
  // refundTarget, refundedRial }. W4 — { target:"wallet" } sends the money back into the
  // buyer's wallet (409 if the owner's flag is off); a WALLET-paid purchase always returns
  // to the wallet whatever is sent. Omitted ⇒ "cash" (today's behaviour).
  refund: (purchaseId, body) =>
    api.post("/v1/market/purchases/" + encodeURIComponent(purchaseId) + "/refund", body || {}),

  // S-MKT-PRO-W4 — what the buy box needs before it renders:
  //   → { walletPayEnabled, refundToWalletEnabled, walletBalanceRial, taxPercentage,
  //       refundWindowDays, refundMaxProgressPercent }. Self-scoped (own balance only).
  // S-CARD W3-b — an optional (courseId, couponCode) turns this into a priced preview:
  //   → adds { listPriceRial, payableRial, memberDiscountRial, discountSource, couponValid,
  //       couponReason }. Omitted courseId ⇒ byte-for-byte the response above.
  checkoutOptions: (courseId, couponCode) => {
    if (!courseId) return api.get("/v1/market/checkout-options");
    const p = new URLSearchParams();
    p.set("courseId", courseId);
    if (couponCode) p.set("couponCode", couponCode);
    return api.get("/v1/market/checkout-options?" + p.toString());
  },
  //   myPurchases() — the buyer's OWN purchase history (receipts, not access) → { items:[{
  //     purchaseId, marketCourseId, title, coverImageUrl, pricePaidRial, taxRial, payMethod,
  //     refundTarget, couponApplied, status, createdAt, completedAt, refundedAt }] }.
  myPurchases: () => api.get("/v1/market/purchases/mine"),
  //   purchaseReceipt(id) — ONE printable receipt (own purchase, or any as admin; 404
  //     no-leak otherwise) → { purchaseId, status, buyerName, title, totalRial, taxRial,
  //     netRial, payMethod, refundTarget, couponApplied, listPriceRial, memberDiscountRial,
  //     discountSource, paymentId, paymentProvider, paymentRef, paymentStatus, paidAt,
  //     createdAt, completedAt, refundedAt }.
  purchaseReceipt: (purchaseId) =>
    api.get("/v1/market/purchases/" + encodeURIComponent(purchaseId) + "/receipt"),

  // READS:
  //   myCourses() — the buyer's active (non-revoked) enrollments → { items:[{
  //     enrollmentId, marketCourseId, title, coverImageUrl, level, status,
  //     progressPercent, grantedAt, completedAt, lastSectionId }] }.
  myCourses: () => api.get("/v1/market/my-courses"),
  //   earningsMine() — the author's accrued share (NOT yet paid; payout is P7) →
  //     { totals:{ salesCount, grossRial, accruedAuthorShareRial },
  //       perCourse:[{ marketCourseId, title, salesCount, grossRial, accruedAuthorShareRial }] }.
  earningsMine: () => api.get("/v1/market/earnings/mine"),
  //   courseAnalytics(id) — AUTHOR-scoped consumption analytics for ONE of the caller's OWN
  //     courses (a non-owner / non-oversight role gets a no-leak 404). AGGREGATE COUNTS ONLY —
  //     no buyer identity / PII. →
  //     { marketCourseId, title, enrolledCount, completedCount, completionRate,
  //       avgProgressPercent,
  //       sections:[{ sectionId, title, moduleTitle, reachedCount, completedCount, dropoffRate }] }.
  //     dropoffRate = % of enrolled buyers who did NOT finish that section.
  courseAnalytics: (id) =>
    api.get("/v1/market/courses/" + encodeURIComponent(id) + "/analytics"),
  //   adminSalesReport() — tenant-wide totals (admin/super_admin) →
  //     { salesCount, refundedCount, grossRial, universityRevenueRial, authorPayableRial }.
  adminSalesReport: () => api.get("/v1/market/admin/sales-report"),

  // --- LEARNING EXPERIENCE / COURSE PLAYER (R-MARKET P6 — doc §9) ---
  // The ENROLLED BUYER's player + progress + notes + completion certificate. EVERY call is
  // scoped server-side to the caller's OWN active enrollment; a non-enrolled / refunded /
  // cross-tenant user gets 404 (no content / progress / notes leak). Media bytes still flow
  // through the existing enrollment-gated assetAccessToken() — the player carries only assetId.
  //
  //   player(id) — the full current-version chart for the buyer (modules → lessons → sections
  //     WITH content refs assetId/richTextBody/externalUrl/quizExamId + contentType +
  //     durationSeconds) + per-section { completed, lastPositionSeconds } + lesson { locked }
  //     (sequential-lock) + progress { progressPercent, lastSectionId, completedAt,
  //     totalSections, completedSections } + certificateEligible/hasCertificate + course
  //     { id, title, coverImageUrl, level, authorName }. 404 if not enrolled.
  player: (id) => api.get("/v1/market/my-courses/" + encodeURIComponent(id) + "/player"),
  //   saveProgress(id, { sectionId, completed?, lastPositionSeconds? }) — upsert one section's
  //     progress (completed defaults true; lastPositionSeconds only overwritten when sent),
  //     recompute the aggregate → returns the updated progress block (same shape as above).
  saveProgress: (id, { sectionId, completed, lastPositionSeconds } = {}) => {
    const body = { sectionId };
    if (completed !== undefined) body.completed = completed;
    if (lastPositionSeconds != null) body.lastPositionSeconds = lastPositionSeconds;
    return api.post(
      "/v1/market/my-courses/" + encodeURIComponent(id) + "/progress",
      body,
    );
  },
  //   notes(id) — the buyer's OWN notes for the course (newest first) → NoteView[]
  //     ({ id, marketCourseId, sectionId, body, videoPositionSeconds, createdAt, updatedAt }).
  notes: (id) => api.get("/v1/market/my-courses/" + encodeURIComponent(id) + "/notes"),
  //   addNote(id, { body, sectionId?, videoPositionSeconds? }) — create a note / video bookmark.
  addNote: (id, body) =>
    api.post("/v1/market/my-courses/" + encodeURIComponent(id) + "/notes", body),
  //   updateNote(id, noteId, { body?, sectionId?, videoPositionSeconds? }) — edit own note.
  updateNote: (id, noteId, body) =>
    api.patch(
      "/v1/market/my-courses/" + encodeURIComponent(id) + "/notes/" + encodeURIComponent(noteId),
      body,
    ),
  //   deleteNote(id, noteId) — delete own note → { ok:true }.
  deleteNote: (id, noteId) =>
    api.delete(
      "/v1/market/my-courses/" + encodeURIComponent(id) + "/notes/" + encodeURIComponent(noteId),
    ),
  //   issueCertificate(id) — issue (idempotent) the «گواهی پایان دورهٔ بازار» (reuses the §20
  //     Credential system). 409 before 100% (and a passing quiz, if any). → cert view
  //     ({ id, serial, type, title, issuer, description, issuedAt, url, status }).
  issueCertificate: (id) =>
    api.post("/v1/market/my-courses/" + encodeURIComponent(id) + "/certificate", {}),
  //   getCertificate(id) — fetch / verify the issued cert. 404 if none has been issued.
  getCertificate: (id) =>
    api.get("/v1/market/my-courses/" + encodeURIComponent(id) + "/certificate"),
  //   certificateRender(id) — S-MKT-PRO-E2: everything the printable «مدرک من» page needs in
  //     one read → { certificate, data:{ studentName, courseTitle, authorName, subtitle,
  //     issuedAt, serial, verifyPath }, template:{ assetId, fields[] } | null }. 404 before
  //     the certificate has actually been issued (POST first). template=null ⇒ the platform's
  //     default branded template is drawn.
  certificateRender: (id) =>
    api.get("/v1/market/my-courses/" + encodeURIComponent(id) + "/certificate/render"),

  // --- Q&A / FORUM + AUTHOR PAYOUT (R-MARKET P7 — FINAL — doc §4.4/§7.6/§9.5 + §5.4) ---
  //
  // Q&A (course pre-purchase + in-player questions). The LIST is PUBLIC (a guest/prospective
  // buyer reads the thread before buying) → publicApi + a REQUIRED tenantSlug (the established
  // public pattern). ASK/REPLY + DELETE are authed (the JWT carries the tenant).
  //
  //   courseQuestions(id, tenantSlug) — @Public: a published course's thread →
  //     { items:[{ id, userId, authorName, body, isAnswerByAuthor, sectionId, createdAt,
  //       replies:[{ id, userId, authorName, body, isAnswerByAuthor, sectionId, createdAt }] }] }.
  //     404 for a draft / cross-tenant / unknown course (no-leak).
  //     E4: the OPTIONAL 3rd arg scopes the read to ONE lesson (top-level rows with that
  //     lessonId, each still carrying all of its replies). Omitted ⇒ the whole-course thread
  //     exactly as before — the in-player tab toggles between the two.
  courseQuestions: (id, tenantSlug, lessonId) =>
    publicApi.get(
      "/v1/market/courses/" + encodeURIComponent(id) + "/questions?tenantSlug=" +
        encodeURIComponent(tenantSlug || "") +
        (lessonId ? "&lessonId=" + encodeURIComponent(lessonId) : ""),
    ),
  //   askQuestion(id, { body, sectionId?, lessonId?, parentId? }) — authed. A top-level question
  //     (no parentId) may be asked by ANY authed user; a reply (parentId set) requires the
  //     course author OR an active enrollment (else 403). isAnswerByAuthor is set server-side.
  //     Returns the created question (with empty replies[]) or the created reply.
  askQuestion: (id, body) =>
    api.post("/v1/market/courses/" + encodeURIComponent(id) + "/questions", body),
  //   deleteQuestion(id, qid) — authed soft-delete (own | course author | moderator) → { ok }.
  //     A top-level question takes its replies with it; a stranger gets a no-leak 404.
  deleteQuestion: (id, qid) =>
    api.delete(
      "/v1/market/courses/" + encodeURIComponent(id) + "/questions/" + encodeURIComponent(qid),
    ),

  // Wave D (Gap 5) — answer pinning + light moderation. Pinned threads/answers sort to the top;
  // a moderator (content_manager / oversight) can HIDE an abusive Q/reply (hidden disappears for
  // learners, stays flagged «پنهان‌شده» on the moderation read). Each view row now also carries
  // pinnedAt + hidden + hiddenAt. All authed; the tenant rides the JWT.
  //
  //   moderationQuestions(id) — REVIEWER full thread INCLUDING hidden rows (each flagged) →
  //     same shape as courseQuestions but with hidden=true rows visible. Non-moderator → 404.
  moderationQuestions: (id) =>
    api.get("/v1/market/courses/" + encodeURIComponent(id) + "/questions/moderation"),
  //   pinQuestion(id, qid) / unpinQuestion(id, qid) — AUTHOR pins/unpins (author of the course
  //     only; else 404 no-leak) → { ok, id, pinned }.
  pinQuestion: (id, qid) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/questions/" + encodeURIComponent(qid) + "/pin",
    ),
  unpinQuestion: (id, qid) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/questions/" + encodeURIComponent(qid) + "/unpin",
    ),
  //   hideQuestion(id, qid) / unhideQuestion(id, qid) — REVIEWER hides/unhides (content_manager /
  //     oversight only; else 404 no-leak) → { ok, id, hidden }.
  hideQuestion: (id, qid) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/questions/" + encodeURIComponent(qid) + "/hide",
    ),
  unhideQuestion: (id, qid) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/questions/" + encodeURIComponent(qid) + "/unhide",
    ),

  // Wave D (Gap 6) — downloadable lesson resources / handouts. The author attaches an EXISTING
  // uploaded asset (POST /v1/market/assets) to a lesson with a title; an ENROLLED buyer lists +
  // downloads each behind the SAME token-gated /assets/:id/stream (non-enrolled → 404 no-leak).
  //
  //   AUTHOR (instructor / content_manager / admin / super_admin):
  //   lessonResourcesAuthor(id, lessonId) — author list →
  //     { items:[{ id, lessonId, assetId, title, order, fileName, sizeBytes, mimeType, assetType, createdAt }] }.
  lessonResourcesAuthor: (id, lessonId) =>
    api.get(
      "/v1/market/courses/" + encodeURIComponent(id) + "/lessons/" + encodeURIComponent(lessonId) + "/resources",
    ),
  //   attachResource(id, lessonId, { assetId, title, order? }) → the created AuthorResourceView.
  //     409 if the same asset is already attached to this lesson.
  attachResource: (id, lessonId, body) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/lessons/" + encodeURIComponent(lessonId) + "/resources",
      body,
    ),
  //   deleteResource(id, lessonId, resourceId) → { ok } (soft-delete; the asset is untouched).
  deleteResource: (id, lessonId, resourceId) =>
    api.delete(
      "/v1/market/courses/" + encodeURIComponent(id) + "/lessons/" + encodeURIComponent(lessonId) +
        "/resources/" + encodeURIComponent(resourceId),
    ),
  //   reorderResources(id, lessonId, { orderedIds }) → { ok }.
  reorderResources: (id, lessonId, body) =>
    api.patch(
      "/v1/market/courses/" + encodeURIComponent(id) + "/lessons/" + encodeURIComponent(lessonId) +
        "/resources/reorder",
      body,
    ),
  //   ENROLLED BUYER: lessonResources(id, lessonId) — list WITH per-file token-gated download
  //     urls → { items:[{ id, title, order, fileName, sizeBytes, mimeType, assetType,
  //     downloadUrl, expiresAt, createdAt }] }. A non-enrolled / refunded user → 404 (no-leak).
  lessonResources: (id, lessonId) =>
    api.get(
      "/v1/market/my-courses/" + encodeURIComponent(id) + "/lessons/" + encodeURIComponent(lessonId) + "/resources",
    ),

  // AUTHOR PAYOUT (§5.4 تسویه با استاد). The §5.3 author share ACCRUES to the ledger account
  // 2300 «پرداختنی به استاد» on each confirmed purchase (P5); these settle it out.
  //
  //   ADMIN/FINANCE (admin | super_admin | finance_manager):
  //   adminPayouts() — authors with an accrued/paid/unpaid balance + recent payout history →
  //     { authors:[{ authorUserId, authorName, coursesCount, accruedRial, paidRial, unpaidRial,
  //       grossRial, universityShareRial, lastPayoutAt }],
  //       payouts:[{ id, authorUserId, authorName, grossSalesRial, universityShareRial,
  //       authorShareRial, status, paidAt, ledgerEntryId, createdAt, … }] }.
  adminPayouts: () => api.get("/v1/market/admin/payouts"),
  //   runPayout(authorUserId) — settle the author's whole accrued-unpaid balance: books a
  //     BALANCED ledger entry (DEBIT 2300 / CREDIT 1000) inside a Serializable txn (double-pay
  //     safe). → { ok, payoutId, authorShareRial, ledgerEntryId, status:"paid" }. 409 if nothing
  //     is owed («مبلغی برای تسویه نیست»); 404 for a cross-tenant author; 403 for a non-finance role.
  runPayout: (authorUserId) => api.post("/v1/market/admin/payouts", { authorUserId }),
  //   payoutsMine() — the caller's OWN payout history (most recent first) →
  //     { items:[{ id, periodFrom, periodTo, grossSalesRial, universityShareRial,
  //       authorShareRial, status, paidAt, ledgerEntryId, createdAt,
  //       adjustmentRial, netPaidRial }] }.  ← W8 adds the last two: how much of that
  //     settlement was withheld to clear a clawback, and what actually reached the author.
  payoutsMine: () => api.get("/v1/market/payouts/mine"),

  // ===========================================================================
  // S-MKT-PRO-W8 — AUTHOR MONEY: holdback · author-initiated payout requests ·
  // clawback (refund after settlement) · the merged transactions index.
  //
  // Accounting note the whole surface rests on: an author's share ACCRUES to 2300
  // «پرداختنی به استاد». A refund INSIDE the normal window just reverses it off 2300.
  // A refund that lands AFTER the share was already settled has nothing left to
  // reverse, so it books a receivable FROM the author on 1350 «طلب از مؤلف» — the
  // buyer is still refunded immediately from platform funds either way. The 1350
  // claim is then recovered out of the author's NEXT settlements (debt first, cash
  // after), or written off by an admin (Dr 5200 bad-debt). No collection automation.
  // ===========================================================================

  //   AUTHOR (self-scoped — every one of these reads/writes only the caller's own money):
  //   payoutSummary() — the author's full money position →
  //     { accruedRial, paidRial, pendingRial, availableRial, heldRial,
  //       outstandingAdjustmentRial, holdbackDays, payoutMinRial,
  //       openRequest: { id, amountRial, status, createdAt } | null }.
  //     `heldRial` is still inside the holdback window (shown, never payable);
  //     `availableRial` is what a request may ask for right now.
  payoutSummary: () => api.get("/v1/market/payouts/summary"),
  //   requestPayout({ note }) — ask finance to settle the whole available balance.
  //     → { ok, requestId, amountRial, status:"requested" }. 409 when nothing is
  //     available, when the balance is under payoutMinRial, or when a request is
  //     already open (ONE open request per author, enforced by a partial unique index).
  requestPayout: (body) => api.post("/v1/market/payouts/requests", body || {}),
  //   myPayoutRequests() — the caller's OWN requests →
  //     { items:[{ id, amountRial, status, note, reviewNote, reviewedAt, payoutId, createdAt }] }.
  myPayoutRequests: () => api.get("/v1/market/payouts/requests/mine"),
  //   cancelPayoutRequest(id) — withdraw an own request while it is still «requested».
  //     → { ok, requestId, status:"cancelled" }. 409 once finance has acted on it.
  cancelPayoutRequest: (id) =>
    api.delete("/v1/market/payouts/requests/" + encodeURIComponent(id)),
  //   myAdjustments() — the author's OWN clawback deductions, in plain Persian (the
  //     owner's §1.2 rule: never surprise an author with a shrunken settlement) →
  //     { items:[{ id, amountRial, recoveredRial, remainingRial, status, reason,
  //       sourcePurchaseId, sourceBundlePurchaseId, createdAt }] }.
  myAdjustments: () => api.get("/v1/market/payouts/adjustments/mine"),

  //   FINANCE (admin | super_admin | finance_manager) — the request queue:
  //   payoutRequestQueue({ status }) — pending (default) or any status →
  //     { items:[{ id, authorUserId, authorName, amountRial, currentPayableRial,
  //       currentAdjustmentRial, status, note, reviewNote, reviewedBy, reviewedAt,
  //       payoutId, createdAt }] }. `current*` are recomputed LIVE — the amount the
  //     author asked for may be stale by the time finance looks at it.
  payoutRequestQueue: (status) =>
    api.get("/v1/market/admin/payout-requests" + (status ? "?status=" + encodeURIComponent(status) : "")),
  //   reviewPayoutRequest(id, { approve, reviewNote }) — approve runs the real settlement
  //     (recovery first, then cash) → { ok, requestId, status:"approved", payoutId,
  //     authorShareRial, adjustmentRial, netPaidRial }; reject REQUIRES a reviewNote →
  //     { ok, requestId, status:"rejected" }.
  reviewPayoutRequest: (id, body) =>
    api.post("/v1/market/admin/payout-requests/" + encodeURIComponent(id) + "/review", body),

  //   FINANCE — the clawback (1350) book:
  //   clawbackReport() — aging + every claim →
  //     { totals:{ outstandingRial, recoveredRial, writtenOffRial },
  //       aging:{ d0_30, d31_60, d61_90, d90_plus },
  //       items:[{ id, authorUserId, authorName, amountRial, recoveredRial, remainingRial,
  //         status, reason, bucket, createdAt }] }.
  clawbackReport: () => api.get("/v1/market/admin/clawbacks"),
  //   writeOffClawback(id, { reason }) — admin|super_admin ONLY (finance_manager may
  //     settle but may not forgive). Reason is MANDATORY. Books Dr 5200 / Cr 1350 →
  //     { ok, adjustmentId, status:"written_off", writtenOffRial, ledgerEntryId }.
  writeOffClawback: (id, body) =>
    api.post("/v1/market/admin/clawbacks/" + encodeURIComponent(id) + "/write-off", body),

  //   TRANSACTIONS INDEX (self-scoped, §3) — ONE server-merged, cursor-paginated
  //   timeline over four sources (purchases · wallet · payouts · withdrawals) plus the
  //   clawback deductions. The merge is server-side ON PURPOSE: four client fetches
  //   stitched together cannot paginate correctly.
  //   myTransactions({ type, from, to, cursor, limit }) →
  //     { items:[{ key, type, at, title, amountRial, status, link, note }],
  //       nextCursor, sections:{ purchase, wallet, payout, withdrawal, clawback } }.
  //     `sections` is role-aware: an author-less user gets no payout/clawback tab.
  //     `link` deep-links each row to its own canonical detail page — this page is the
  //     time index, those pages are the detail.
  myTransactions: ({ type, from, to, cursor, limit } = {}) => {
    const q = [];
    if (type) q.push("type=" + encodeURIComponent(type));
    if (from) q.push("from=" + encodeURIComponent(from));
    if (to) q.push("to=" + encodeURIComponent(to));
    if (cursor) q.push("cursor=" + encodeURIComponent(cursor));
    if (limit) q.push("limit=" + encodeURIComponent(limit));
    return api.get("/v1/market/transactions/mine" + (q.length ? "?" + q.join("&") : ""));
  },
  //   myTransactionsCsv({ type, from, to }) — the SAME rows as a CSV export. The caller's
  //     own data only (the owner's §3.5 rule); never another user's.
  myTransactionsCsv: ({ type, from, to } = {}) => {
    const q = [];
    if (type) q.push("type=" + encodeURIComponent(type));
    if (from) q.push("from=" + encodeURIComponent(from));
    if (to) q.push("to=" + encodeURIComponent(to));
    return api.get("/v1/market/transactions/mine.csv" + (q.length ? "?" + q.join("&") : ""));
  },

  // ===========================================================================
  // R-MARKET FEATURE EXTENSIONS (additive §10.3) — buyer reviews / coupons /
  // wishlist / categories. RBAC is enforced SERVER-SIDE on every call (the UI
  // mirrors it); the public reviews READ rides on courseReviews() above (it now
  // returns the REAL distribution + items). All write paths are JWT-guarded.
  // ===========================================================================

  // --- REVIEWS (buyer star-reviews; §7.6) ---
  //   postReview(id, { rating, comment? }) — enrolled buyer UPSERTS their OWN review (one per
  //     user per course). 403 for the course author / a non-enrolled user; 404 for an
  //     unpublished/cross-tenant course. → the created/updated review item.
  postReview: (id, body) =>
    api.post("/v1/market/courses/" + encodeURIComponent(id) + "/reviews", body),
  //   replyReview(id, rid, { reply? }) — the COURSE AUTHOR replies to a review on their course
  //     (404 no-leak for anyone else). An empty reply clears it. → the updated review item.
  replyReview: (id, rid, body) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/reviews/" + encodeURIComponent(rid) + "/reply",
      body || {},
    ),
  //   hideReview / unhideReview(id, rid, { reason? }) — moderator|content_manager|admin|
  //     super_admin only (drops/re-adds the review from/to the public list + recompute).
  hideReview: (id, rid, body) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/reviews/" + encodeURIComponent(rid) + "/hide",
      body || {},
    ),
  unhideReview: (id, rid) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/reviews/" + encodeURIComponent(rid) + "/unhide",
      {},
    ),

  // --- COUPONS (discount codes; §5) ---
  //   createCoupon({ code, marketCourseId?, description?, percentOff?|amountOffRial?,
  //     validUntil?, maxUses?, maxUsesPerUser? }) — course-specific (marketCourseId set) →
  //     the course author or admin; global (omit marketCourseId) → admin/super_admin only.
  //     Exactly ONE of percentOff (1..100) | amountOffRial (>0). → the created coupon.
  //     W8 — `maxUsesPerUser` caps redemptions PER BUYER (null/0 ⇒ unlimited, i.e. exactly
  //     the pre-W8 behaviour). Enforced server-side against MarketCouponRedemption; a
  //     refunded purchase releases its redemption so the buyer is not charged a use.
  createCoupon: (body) => api.post("/v1/market/coupons", body),
  //   couponsMine() — the caller's OWN coupons (those they created) → { items:[CouponView] }.
  couponsMine: () => api.get("/v1/market/coupons/mine"),
  //   adminCoupons() — admin/super_admin: all tenant coupons → { items:[CouponView] }.
  adminCoupons: () => api.get("/v1/market/admin/coupons"),
  //   deactivateCoupon(id) / activateCoupon(id) — PATCH active (creator/admin; 404 no-leak).
  deactivateCoupon: (id) =>
    api.patch("/v1/market/coupons/" + encodeURIComponent(id), { active: false }),
  activateCoupon: (id) =>
    api.patch("/v1/market/coupons/" + encodeURIComponent(id), { active: true }),
  //   deleteCoupon(id) — DELETE (creator/admin; 404 no-leak) → { ok, id }.
  deleteCoupon: (id) => api.delete("/v1/market/coupons/" + encodeURIComponent(id)),
  //   validateCoupon({ code, marketCourseId }) — authed buyer preview. NEVER throws on an
  //     invalid code — returns { valid:false, reason } so the buyer sees a friendly message;
  //     on a valid code → { valid:true, couponId, listPriceRial, discountedPriceRial, discountRial }.
  //     Throws only on a missing/cross-tenant course (404).
  validateCoupon: (body) => api.post("/v1/market/coupons/validate", body),

  // --- WISHLIST (buyer "save for later"; §7) — any authed user, OWN list only ---
  //   wishlist() — the caller's own items + a light course card each →
  //     { items:[{ id, marketCourseId, title, coverImageUrl, level, pricingModel, priceRial, addedAt }] }.
  wishlist: () => api.get("/v1/market/wishlist"),
  //   addWishlist(courseId) — idempotent add (404 if not published) → { ok, id, added }.
  addWishlist: (courseId) =>
    api.post("/v1/market/wishlist/" + encodeURIComponent(courseId), {}),
  //   removeWishlist(courseId) — remove own item (404 if not in the caller's list) → { ok, id }.
  removeWishlist: (courseId) =>
    api.delete("/v1/market/wishlist/" + encodeURIComponent(courseId)),

  // --- CART (S-MKT-PRO-W9 «سبد خرید سبک») — any authed user, OWN cart only ---
  //
  // ONE payment for the whole cart; the server books ONE §5.3 ledger entry PER LINE, so a
  // cart purchase is identical to N single purchases (per-item refund, receipts and the
  // author payout all keep working unchanged). A cart line stores NO price: every response
  // below is priced LIVE and re-validates each line's coupon, so what the cart shows is
  // exactly what checkout charges.
  //
  //   cart() — the caller's own cart →
  //     { items:[{ id, marketCourseId, title, coverImageUrl, level, pricingModel,
  //                listPriceRial, pricePaidRial, discountRial, couponId, couponCode,
  //                blockedReason, addedAt }],
  //       count, totalListRial, totalPayableRial, totalDiscountRial, blockedCount,
  //       walletPayEnabled, walletBalanceRial }.
  //     `blockedReason` (Persian, null = ready) = a line that cannot be bought right now
  //     (unpublished / already owned / your own course / no price / its coupon went bad).
  cart: () => api.get("/v1/market/cart"),
  //   addToCart(courseId, couponCode?) — idempotent add → { ok, id, added, cart }. 404 if the
  //     course isn't published; 409 for your own course or one you already own.
  addToCart: (courseId, couponCode) =>
    api.post("/v1/market/cart/items", { courseId, couponCode: couponCode || null }),
  //   setCartCoupon(courseId, couponCode|null) — attach or CLEAR this line's coupon (400 with
  //     a Persian reason if the code is invalid for that course) → the refreshed cart.
  setCartCoupon: (courseId, couponCode) =>
    api.patch(
      "/v1/market/cart/items/" + encodeURIComponent(courseId) + "/coupon",
      { couponCode: couponCode || null },
    ),
  //   removeFromCart(courseId) — remove one line (404 if not in the caller's cart) → the cart.
  removeFromCart: (courseId) =>
    api.delete("/v1/market/cart/items/" + encodeURIComponent(courseId)),
  //   clearCart() — empty the cart → the (now empty) cart.
  clearCart: () => api.delete("/v1/market/cart"),
  //   checkoutCart({ payMethod?, idempotencyKey? }) — buy the WHOLE cart:
  //     all-free cart ⇒ { free:true, itemCount, purchaseIds, status:"completed", enrolled:true }
  //     wallet        ⇒ { free:false, payMethod:"wallet", paymentId, purchaseIds, amount,
  //                       status:"completed", enrolled:true, walletBalanceRial }
  //     gateway       ⇒ { free:false, payMethod:"gateway", paymentId, purchaseIds, amount,
  //                       redirectUrl, isMock, provider } — send the browser to redirectUrl
  //                     (the SHARED market callback confirms every line at once).
  //     A 409 NAMES any line that is no longer buyable — the whole checkout refuses rather
  //     than silently dropping an item. `idempotencyKey` replays the first outcome instead
  //     of charging twice (the replay carries { replayed:true }).
  checkoutCart: (body) => api.post("/v1/market/cart/checkout", body || {}),

  // --- CATEGORIES (catalog taxonomy; §7.2) — public read; admin CRUD ---
  //   categories(tenantSlug) — @Public: the tenant's ACTIVE categories (catalog filter) →
  //     { items:[{ id, name, slug, order, active }] }.
  categories: (tenantSlug) =>
    publicApi.get("/v1/market/categories?tenantSlug=" + encodeURIComponent(tenantSlug || "")),
  //   adminCategories() — admin/super_admin: all categories (active + inactive).
  adminCategories: () => api.get("/v1/market/admin/categories"),
  //   adminCreateCategory({ name, slug, order?, active? }) — admin/super_admin.
  adminCreateCategory: (body) => api.post("/v1/market/admin/categories", body),
  //   adminUpdateCategory(id, { name?, slug?, order?, active? }) — admin/super_admin.
  adminUpdateCategory: (id, body) =>
    api.patch("/v1/market/admin/categories/" + encodeURIComponent(id), body),
  //   adminDeleteCategory(id) — admin/super_admin → { ok, id }.
  adminDeleteCategory: (id) =>
    api.delete("/v1/market/admin/categories/" + encodeURIComponent(id)),

  // --- CATALOG (P4 EXTENDED) — real category + price filtering ---
  //   catalogExt({ tenantSlug, q?, category?, level?, language?, sort?, free?, priceMax?,
  //     page?, limit? }) — same shape as catalog() but forwards the now-REAL category id +
  //     free (boolean) + priceMax (whole rial) filters.
  catalogExt: ({ tenantSlug, q, category, level, language, sort, free, priceMax, page, limit, tags } = {}) => {
    const p = new URLSearchParams();
    p.set("tenantSlug", tenantSlug || "");
    if (q) p.set("q", q);
    if (category) p.set("category", category);
    if (level) p.set("level", level);
    if (language) p.set("language", language);
    if (sort) p.set("sort", sort);
    if (free) p.set("free", "true");
    if (priceMax != null && priceMax > 0) p.set("priceMax", String(priceMax));
    if (page) p.set("page", String(page));
    if (limit) p.set("limit", String(limit));
    // S-MKT-PRO-W2 — comma-separated tag SLUGS (AND semantics; each chip narrows).
    if (tags) p.set("tags", Array.isArray(tags) ? tags.join(",") : tags);
    return publicApi.get("/v1/market/catalog?" + p.toString());
  },

  // ===========================================================================
  // S-MKT-PRO-W2 — tags + related + instructor showcase + author profile
  // ===========================================================================
  //   publicTags(tenantSlug) — @Public: tag chips in use on ≥1 PUBLISHED course →
  //     { items:[{ id, name, slug, courseCount }] } (usage desc, ≤30).
  publicTags: (tenantSlug) =>
    publicApi.get("/v1/market/tags?tenantSlug=" + encodeURIComponent(tenantSlug || "")),
  //   relatedCourses(id, tenantSlug) — @Public: deterministic related rail (shared tags >
  //     same category; enrollment-ranked; ≤8) → { items:[MarketCatalogCard] }.
  relatedCourses: (id, tenantSlug) =>
    publicApi.get(
      "/v1/market/courses/" + encodeURIComponent(id) + "/related?tenantSlug=" +
        encodeURIComponent(tenantSlug || ""),
    ),
  //   instructorPublic(userId, tenantSlug) — @Public «ویترین مدرس»: 404 unless the user has
  //     ≥1 published course → { userId, name, headline, bio, avatarUrl, social, stats:{...},
  //     courses:[MarketCatalogCard] }. NO PII.
  instructorPublic: (userId, tenantSlug) =>
    publicApi.get(
      "/v1/market/instructors/" + encodeURIComponent(userId) + "?tenantSlug=" +
        encodeURIComponent(tenantSlug || ""),
    ),
  //   authoringTags() — author roles: the tenant's full tag vocabulary (studio picker) →
  //     { items:[{ id, name, slug }] }.
  authoringTags: () => api.get("/v1/market/authoring/tags"),
  //   courseTags(id) / setCourseTags(id, tagIds) — the course's attached tag set (author/
  //     oversight; replace-set write, ≤8 ids from the admin-managed vocabulary).
  courseTags: (id) => api.get("/v1/market/courses/" + encodeURIComponent(id) + "/tags"),
  setCourseTags: (id, tagIds) =>
    api.put("/v1/market/courses/" + encodeURIComponent(id) + "/tags", { tagIds }),
  //   authorProfile() / updateAuthorProfile({ headline?, bioMarkdown?, social? }) — the
  //     caller's OWN showcase profile (studio Settings). social keys are whitelisted
  //     server-side (website|linkedin|twitter|youtube|instagram|telegram, http(s) only).
  authorProfile: () => api.get("/v1/market/authoring/profile"),
  updateAuthorProfile: (body) => api.put("/v1/market/authoring/profile", body),
  //   adminTags()/adminCreateTag/adminUpdateTag/adminDeleteTag — admin vocabulary CRUD
  //     (mirrors the category admin; delete also detaches the tag from every course).
  adminTags: () => api.get("/v1/market/admin/tags"),
  adminCreateTag: (body) => api.post("/v1/market/admin/tags", body),
  adminUpdateTag: (id, body) =>
    api.patch("/v1/market/admin/tags/" + encodeURIComponent(id), body),
  adminDeleteTag: (id) => api.delete("/v1/market/admin/tags/" + encodeURIComponent(id)),

  // ===========================================================================
  // R-MARKET BUNDLES — «بستهٔ دوره» course bundles (doc §۸.۶ / backend
  // market-bundle.controller.ts + market-bundle.service.ts). An author packages a
  // set of THEIR OWN published courses into a MarketBundle priced at a single
  // bundle priceRial (typically LESS than the sum of the per-course prices — the
  // «savings»). A buyer pays ONCE (REUSING the existing course Payment/checkout
  // flow + the shared @Public GET /market/purchase/callback — NO new callback)
  // and on a confirmed payment is GRANTED a MarketEnrollment for EVERY course in
  // the bundle (idempotent). Revenue-share flows to the §5.3 Ledger like a course
  // purchase (v1 = bundle-author-only).
  //
  // Every view is the backend BundleDetailView:
  //   { id, tenantId, authorUserId, title, description, coverImageUrl, priceRial,
  //     status, slug, publishedAt, enrollmentCount,
  //     items:[{ itemId, marketCourseId, order, title, coverImageUrl, level, slug, priceRial }],
  //     sumOfIndividualRial, savingsRial }.
  // ===========================================================================

  // --- AUTHOR CRUD (instructor|content_manager|admin|super_admin — own bundle; 404 else) ---
  //   bundlesMine() — the caller's OWN bundles (newest first), each with price math → BundleDetailView[].
  bundlesMine: () => api.get("/v1/market/bundles/mine"),
  //   bundle(id) — own-bundle detail → BundleDetailView (no-leak 404 cross-author/tenant).
  bundle: (id) => api.get("/v1/market/bundles/" + encodeURIComponent(id)),
  //   createBundle({ title*, description?, coverImageUrl?, priceRial? }) — a DRAFT bundle
  //     (priceRial defaults 0 = free). 201 → the new bundle's BundleDetailView.
  createBundle: (body) => api.post("/v1/market/bundles", body),
  //   updateBundle(id, { title?, description?, coverImageUrl?, priceRial? }) — edit metadata/price.
  //     Editable only while DRAFT (a published bundle's price/items are locked → 409). → BundleDetailView.
  updateBundle: (id, body) =>
    api.patch("/v1/market/bundles/" + encodeURIComponent(id), body),
  //   setBundlePrice(id, priceRial) — set just the server-side bundle price (whole rial; 0=free). → BundleDetailView.
  setBundlePrice: (id, priceRial) =>
    api.put("/v1/market/bundles/" + encodeURIComponent(id) + "/price", { priceRial }),
  //   addBundleItem(id, marketCourseId) — add ONE of the author's OWN PUBLISHED courses. 400 for a
  //     foreign/unpublished/missing course (no-leak); 409 if already in the bundle. → BundleDetailView.
  addBundleItem: (id, marketCourseId) =>
    api.post("/v1/market/bundles/" + encodeURIComponent(id) + "/items", { marketCourseId }),
  //   removeBundleItem(id, marketCourseId) — drop a course from the bundle (draft only). → BundleDetailView.
  removeBundleItem: (id, marketCourseId) =>
    api.delete(
      "/v1/market/bundles/" + encodeURIComponent(id) + "/items/" + encodeURIComponent(marketCourseId),
    ),
  //   publishBundle(id) — draft → published (requires ≥2 items; 400 else). → BundleDetailView.
  publishBundle: (id) =>
    api.post("/v1/market/bundles/" + encodeURIComponent(id) + "/publish", {}),
  //   deleteBundle(id) — soft-delete the caller's OWN bundle → { ok:true }.
  deleteBundle: (id) => api.delete("/v1/market/bundles/" + encodeURIComponent(id)),

  // --- PURCHASE (any authed buyer) — reuses the course gateway/mock-confirm flow ---
  //   purchaseBundle(id) — begin a bundle buy. The server uses the SERVER-SIDE bundle price.
  //     free ⇒ { free:true, purchaseId, status:"completed", enrolled:true, coursesGranted, pricePaidRial:0 }
  //     paid ⇒ { free:false, purchaseId, paymentId, redirectUrl, isMock, provider, amount, pricePaidRial }
  //            — redirect the browser to redirectUrl (the SAME gateway/mock flow; the shared
  //            /market/purchase/callback grants EVERY course + books the §5.3 ledger on confirm).
  purchaseBundle: (id) =>
    api.post("/v1/market/bundles/" + encodeURIComponent(id) + "/purchase", {}),

  // --- PUBLIC (guest + any role; resolved by ?tenantSlug — the established public pattern) ---
  //   bundlesPublic(tenantSlug) — the tenant's PUBLISHED bundles → BundleDetailView[].
  bundlesPublic: (tenantSlug) =>
    publicApi.get("/v1/market/bundles/public?tenantSlug=" + encodeURIComponent(tenantSlug || "")),
  //   bundlePublic(idOrSlug, tenantSlug) — a published bundle's public detail (items + price +
  //     sum-of-individual + savings). 404 if missing/not published/cross-tenant (no-leak).
  bundlePublic: (idOrSlug, tenantSlug) =>
    publicApi.get(
      "/v1/market/bundles/" + encodeURIComponent(idOrSlug) + "/public?tenantSlug=" +
        encodeURIComponent(tenantSlug || ""),
    ),

  // ===========================================================================
  // R-MARKET OPEN-RESPONSE RUBRIC GRADING (Gap 9 — doc §9, backend
  // market-open-response.controller.ts + market-open-response.service.ts). A section
  // whose contentType is "open_response" is an essay/task graded against a RUBRIC.
  // RBAC is enforced SERVER-SIDE on every call (the UI mirrors it); no-leak 404s.
  //
  // The RUBRIC view (author + learner, same shape):
  //   { id, sectionId, marketCourseId, title, description, criteria:[{ title, maxPoints,
  //     levels?:[{ label, points }] }], gradingMode:"staff"|"self"|"peer",
  //     peersPerSubmission, maxTotalPoints }.
  // A GRADE view (the grader's identity is OMITTED for peer grades):
  //   { graderRole, scores:[{ criterionIndex, points }], totalPoints, feedback, gradedAt }.
  // ===========================================================================

  // --- AUTHOR — set / read / clear the rubric on an open_response section ---
  //   getRubric(id, sectionId) — the author's rubric for an OWNED open_response section,
  //     or null when unset. 404 cross-author/cross-tenant/non-open_response section.
  getRubric: (id, sectionId) =>
    api.get(
      "/v1/market/courses/" + encodeURIComponent(id) + "/sections/" + encodeURIComponent(sectionId) + "/rubric",
    ),
  //   setRubric(id, sectionId, { title*, description?, criteria:[{ title*, maxPoints*,
  //     levels?:[{ label*, points* }] }], gradingMode?, peersPerSubmission? }) — create/REPLACE
  //     the rubric + grading mode. Per-criterion maxPoints + level rules re-validated server-side
  //     (400 on a malformed payload). → the RubricView (with the server-computed maxTotalPoints).
  setRubric: (id, sectionId, body) =>
    api.put(
      "/v1/market/courses/" + encodeURIComponent(id) + "/sections/" + encodeURIComponent(sectionId) + "/rubric",
      body,
    ),
  //   clearRubric(id, sectionId) — soft-delete the rubric (clears the task config) → { ok:true }.
  clearRubric: (id, sectionId) =>
    api.delete(
      "/v1/market/courses/" + encodeURIComponent(id) + "/sections/" + encodeURIComponent(sectionId) + "/rubric",
    ),

  // --- STAFF GRADING — the course author/oversight grades the queue (own course → 404 else) ---
  //   gradingQueue(id, includeGraded?) — the ungraded-submission queue for an OWNED course →
  //     { items:[{ responseId, sectionId, sectionTitle, learnerUserId, learnerName, body,
  //       attachmentAssetId, status, submittedAt, hasStaffGrade }] }. includeGraded=true also
  //       returns already-graded rows (so the author can revise a grade).
  gradingQueue: (id, includeGraded) =>
    api.get(
      "/v1/market/courses/" + encodeURIComponent(id) + "/grading-queue" +
        (includeGraded ? "?includeGraded=true" : ""),
    ),
  //   gradeResponse(id, responseId, { scores:[{ criterionIndex, points }], feedback? }) — a STAFF
  //     grade (AUTHORITATIVE — flips the submission to "graded", locking resubmits). Per-criterion
  //     points server-validated ≤ that criterion's maxPoints; total is the server SUM (a client
  //     total is ignored). Idempotent per grader (a re-grade UPDATES). → the GradeView.
  gradeResponse: (id, responseId, body) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/responses/" + encodeURIComponent(responseId) + "/grade",
      body,
    ),

  // --- ENROLLED LEARNER — submit / read / self-grade / peer-grade (enrollment gate in service) ---
  //   openResponseMine(id, sectionId) — the caller's OWN submission + the rubric + any grade they
  //     may see → { rubric, submission:{ id, body, attachmentAssetId, status, submittedAt,
  //     updatedAt }|null, grade:GradeView|null, peerGrades:GradeView[]|null }. A non-enrolled /
  //     refunded / cross-tenant caller → 404 (no-leak). Peer grader identities are NEVER surfaced.
  openResponseMine: (id, sectionId) =>
    api.get(
      "/v1/market/my-courses/" + encodeURIComponent(id) + "/sections/" + encodeURIComponent(sectionId) + "/open-response",
    ),
  //   submitOpenResponse(id, sectionId, { body?, attachmentAssetId? }) — submit OR resubmit (one
  //     active submission per learner per section; a resubmit REPLACES in place while not graded —
  //     409 once a staff grade lands). At least a body OR a file is required. → the MyOpenResponseView.
  submitOpenResponse: (id, sectionId, body) =>
    api.put(
      "/v1/market/my-courses/" + encodeURIComponent(id) + "/sections/" + encodeURIComponent(sectionId) + "/open-response",
      body,
    ),
  //   selfGradeOpenResponse(id, sectionId, { scores, feedback? }) — the learner self-assesses their
  //     OWN submission (only when gradingMode="self"; 403 otherwise). Does NOT lock. → the GradeView.
  selfGradeOpenResponse: (id, sectionId, body) =>
    api.post(
      "/v1/market/my-courses/" + encodeURIComponent(id) + "/sections/" + encodeURIComponent(sectionId) + "/open-response/self-grade",
      body,
    ),
  //   peerTasks(id, sectionId) — the peer-grading task list for a peer-mode open_response section:
  //     fellow learners' submissions the caller may grade (NOT their own; submitter ANONYMIZED) →
  //     { items:[{ responseId, sectionId, sectionTitle, anonymousLabel, body, attachmentAssetId,
  //       rubric, myGrade:GradeView|null }] }. [] when not peer mode / nothing to grade.
  peerTasks: (id, sectionId) =>
    api.get(
      "/v1/market/my-courses/" + encodeURIComponent(id) + "/sections/" + encodeURIComponent(sectionId) + "/peer-tasks",
    ),
  //   peerGradeResponse(id, responseId, { scores, feedback? }) — a DIFFERENT enrolled learner grades
  //     a fellow learner's submission (gradingMode="peer", under peersPerSubmission cap; 403/409
  //     otherwise). The submitter never learns who graded. → the GradeView.
  peerGradeResponse: (id, responseId, body) =>
    api.post(
      "/v1/market/my-courses/" + encodeURIComponent(id) + "/responses/" + encodeURIComponent(responseId) + "/peer-grade",
      body,
    ),
};

// S-CT-W1 — «الترجمة العربية»: the human-review surface over the layer-B content
// translation engine. Read-side (`meta.translation` on the public course/player
// payloads) needs no client of its own — it rides along with those responses.
// Only the AUTHOR-side review tab needs real endpoints, and they are all scoped
// to one course so the studio never has to know about entityType/entityId
// pagination. RBAC + tenant isolation are enforced server-side; a non-owner gets
// a plain 404 (no-leak), exactly like every other studio call.
export const contentI18nApi = {
  //   listForCourse(courseId, locale) — every registered field of the course and its
  //     chart → { courseId, tgtLang, rows[] }, each row
  //     { id, entityType, entityId, field, source, text, status, provider, model,
  //       error, updatedAt }. A registered field with no row yet comes back with
  //     id:null and the synthetic status "missing". Empty `rows` for an unpublished
  //     course is expected, not an error.
  listForCourse: (courseId, locale) =>
    api.get(
      "/v1/i18n/content/courses/" + encodeURIComponent(courseId) +
        "?locale=" + encodeURIComponent(locale),
    ),
  //   updateField(courseId, { locale, entityType, entityId, field, text }) — a human edit.
  //     Always lands the row on status="reviewed", which the machine pipeline then
  //     refuses to overwrite on its own.
  updateField: (courseId, body) =>
    api.patch("/v1/i18n/content/courses/" + encodeURIComponent(courseId) + "/field", body),
  //   retranslate(courseId, { locale, confirmOverwriteReviewed? }) — re-queue the course.
  //     → { queued, reviewedCount, needsConfirmation }. WITHOUT the flag and with any
  //     row already `reviewed` it queues nothing and answers needsConfirmation:true
  //     (a question, not an HTTP error) — that refusal is the feature (item 8: a
  //     machine run must never silently discard human work).
  retranslate: (courseId, body) =>
    api.post("/v1/i18n/content/courses/" + encodeURIComponent(courseId) + "/retranslate", body),
};
